import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { stor, setActiveShift, flushQueue } from './storage';
import { subscribePatients, migrateIfNeeded, isFirebaseConfigured, logAudit, loadShift } from './firebase';
import { TASK_PRIORITY, STALE_CRITICAL, STALE_DEFAULT } from './constants'; // eslint-disable-line no-unused-vars
import { URG, UORD, C, DTMS, TCOLS, CLIST, CCOL, RESUS_OPTIONS, calcNEWS2, DSUBSPECS, SSCOLS } from './constants';
import { toDay, toTime, newVD, blankPt, migrate, calcAge, hospDay } from './utils';
import { UBadge, CBadge, SLabel, Card, IFld, WBtn, AppShell, TopBar, BackBtn, SyncBar } from './components/Atoms';
import { DesktopShell, DesktopSidebar, DesktopMain, DesktopTopBar, DesktopContent, DesktopSidebarContent } from './components/DesktopShell';
import ShiftGate from './ShiftGate';
import ArchiveView from './views/ArchiveView';
import { archiveDischarged, archivePatient, shouldRunMidnightArchive, markArchiveRan } from './archive';
import IssuesTab from './views/IssuesTab';
import VitalsTab, { calcGCS } from './views/VitalsTab';
import TasksTab  from './views/TasksTab';
import { initPush, requestPushPermission, registerDeviceForShift, unregisterDeviceFromShift, sendPush, syncPrefsToSW } from './push';
import LabsTab   from './views/LabsTab';
import StudiesTab from './views/StudiesTab';
import NotesTab   from './views/NotesTab';
import ReferTab      from './views/ReferTab';
import DischargeTab  from './views/DischargeTab';
import MedsTab   from './views/MedsTab';
import AllTasksView from './views/AllTasksView';
import WardRoundView from './views/WardRoundView';
import AuditView from './views/AuditView';
import FAQView from './views/FAQView';


// ─── Mode Toggle (hoisted to avoid remount flicker on iOS) ───────────────────
function ModeToggle({ viewMode, setVM }) {
  return (
    <button onClick={() => setVM(viewMode === 'mobile' ? 'desktop' : 'mobile')}
      title={viewMode === 'mobile' ? 'Switch to Desktop' : 'Switch to Mobile'}
      style={{ background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '0 9px', fontSize: 12, cursor: 'pointer', color: C.dim, flexShrink: 0, display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}>
      {viewMode === 'mobile' ? '🖥' : '📱'}
    </button>
  );
}

// ─── Census strip ────────────────────────────────────────────────────────────
function Ledger({ pts, tf }) {
  const pool = tf === 'all' ? pts : pts.filter(p => p.team === tf);
  const active = pool.filter(p => p.urgency !== 'discharged' && p.urgency !== 'mortality').length;
  const discharged = pool.filter(p => p.urgency === 'discharged').length;
  const mortality  = pool.filter(p => p.urgency === 'mortality').length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, background: C.bg1, borderBottom: `1px solid ${C.bd}`, padding: '5px 14px', flexShrink: 0, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: 10 }}>Census</span>
      <LedgerPill label="Active" value={active} color="#4A9EFF" />
      <LedgerDiv />
      <LedgerPill label="Discharged" value={discharged} color="#30D158" />
      {mortality > 0 && <><LedgerDiv /><LedgerPill label="Mortality" value={mortality} color="#8E8E93" /></>}
    </div>
  );
}
function LedgerPill({ label, value, color, bold }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, padding: '2px 8px' }}>
      <span style={{ fontSize: 14, fontWeight: bold ? 900 : 800, color }}>{value}</span>
      <span style={{ fontSize: 9, color: color + '99', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    </span>
  );
}
function LedgerDiv() {
  return <span style={{ fontSize: 11, color: '#1C2E42', userSelect: 'none', padding: '0 2px' }}>│</span>;
}

// ─── Compact summary footer ──────────────────────────────────────────────────
// ── Shared ⋯ menu (used in both mobile footer and desktop sidebar) ────────
function MoreMenu({ onLeave, onAudit, onPrint, onWardRound, onArchive, onImport, onBackup, onRequestNotif, onDisableNotif, notifPerm, shiftCode, btnStyle, direction, teams, notifTeam, setNotifTeamPref, notifSs, setNotifSsPref, notifSvc, setNotifSvcPref, subspecs = [], copyTeam, setCopyTeamPref, copySs, setCopySsPref, copySvc, setCopySvcPref, roundTeam, setRoundTeamPref, roundSs, setRoundSsPref, roundSvc, setRoundSvcPref, onFaq }) {
  // Use ref for open state so parent re-renders (e.g. notifPerm change) never reset it
  const openRef = useRef(false);
  const [, forceRender] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [copyOpen,  setCopyOpen]  = useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  const setOpen = (v) => { openRef.current = v; forceRender(n => n + 1); };
  const open = openRef.current;
  const openDown = direction === 'down';

  // Active filter summary pills for collapsed state
  const activeTeam = teams?.find(t => t.id === notifTeam);
  const activeSs   = subspecs?.find(s => s.id === notifSs);
  const activeSvc  = notifSvc !== 'all' ? notifSvc : null;
  const hasFilters = notifTeam !== 'all' || notifSs !== 'all' || notifSvc !== 'all';

  const cpActiveTeam = teams?.find(t => t.id === copyTeam);
  const cpActiveSs   = subspecs?.find(s => s.id === copySs);
  const cpActiveSvc  = copySvc !== 'all' ? copySvc : null;
  const cpHasFilters = copyTeam !== 'all' || copySs !== 'all' || copySvc !== 'all';

  const rdActiveTeam = teams?.find(t => t.id === roundTeam);
  const rdActiveSs   = subspecs?.find(s => s.id === roundSs);
  const rdActiveSvc  = roundSvc !== 'all' ? roundSvc : null;
  const rdHasFilters = roundTeam !== 'all' || roundSs !== 'all' || roundSvc !== 'all';

  const filterPills = (at, as_, av) => (
    <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
      {at && <span style={{ fontSize: 9, fontWeight: 700, color: at.color, background: at.color + '22', border: '1px solid ' + at.color + '44', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{at.name}</span>}
      {as_ && <span style={{ fontSize: 9, fontWeight: 700, color: as_.color, background: as_.color + '22', border: '1px solid ' + as_.color + '44', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{as_.name}</span>}
      {av && <span style={{ fontSize: 9, fontWeight: 700, color: CCOL[av] || '#AAA', background: (CCOL[av] || '#AAA') + '22', border: '1px solid ' + (CCOL[av] || '#AAA') + '44', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{av}</span>}
    </div>
  );

  const filterRows = (tVal, tSet, ssVal, ssSet, svcVal, svcSet) => (
    <div style={{ padding: '0 12px 8px' }}>
      {teams && teams.length > 0 && (
        <div style={{ marginBottom: 5 }}>
          <div style={{ fontSize: 9, color: C.dim2, marginBottom: 3 }}>Team</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => (
              <button key={t.id} onClick={() => tSet(t.id)}
                style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((tVal || 'all') === t.id ? t.color : C.bd), background: (tVal || 'all') === t.id ? t.color + '22' : C.bg0, color: (tVal || 'all') === t.id ? t.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {subspecs.length > 0 && (
        <div style={{ marginBottom: 5 }}>
          <div style={{ fontSize: 9, color: C.dim2, marginBottom: 3 }}>Subspec</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...subspecs].map(s => (
              <button key={s.id} onClick={() => ssSet(s.id)}
                style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((ssVal || 'all') === s.id ? s.color : C.bd), background: (ssVal || 'all') === s.id ? s.color + '22' : C.bg0, color: (ssVal || 'all') === s.id ? s.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 9, color: C.dim2, marginBottom: 3 }}>Service</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => (
            <button key={opt.id} onClick={() => svcSet(opt.id)}
              style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((svcVal || 'all') === opt.id ? opt.color : C.bd), background: (svcVal || 'all') === opt.id ? opt.color + '22' : C.bg0, color: (svcVal || 'all') === opt.id ? opt.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(!open)} style={btnStyle || { padding: '9px 12px', borderRadius: 10, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>&#8943;</button>
      {open && (
        <>
          <div onMouseDown={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
          <div onMouseDown={e => e.stopPropagation()} style={{ position: 'absolute', ...(openDown ? { top: 'calc(100% + 6px)' } : { bottom: '110%' }), right: 0, background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 12, padding: 6, zIndex: 200, minWidth: 220, boxShadow: '0 8px 32px rgba(0,0,0,0.7)' }}>
            {/* Shift code */}
            <div style={{ padding: '6px 12px 10px', borderBottom: '1px solid ' + C.bd, marginBottom: 4 }}>
              <div style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>Database Code</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#4A9EFF', letterSpacing: '0.1em' }}>{shiftCode}</div>
            </div>
            {/* Notifications toggle row — does NOT close menu */}
            <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>🔔</span>
              <span style={{ flex: 1, fontSize: 13, color: C.tx }}>Notifications</span>
              <button
                onClick={() => {
                  if (notifPerm === 'granted') {
                    onDisableNotif && onDisableNotif();
                  } else {
                    onRequestNotif && onRequestNotif();
                  }
                }}
                style={{
                  position: 'relative', width: 32, height: 18, borderRadius: 9, border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0,
                  background: notifPerm === 'granted' ? '#30D158' : '#3A3A4A',
                  transition: 'background 0.2s',
                }}>
                <span style={{
                  position: 'absolute', top: 2, left: notifPerm === 'granted' ? 15 : 2,
                  width: 14, height: 14, borderRadius: '50%', background: '#FFF',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }} />
              </button>
            </div>
            {/* Notification filters — only when on, directly under toggle */}
            {notifPerm === 'granted' && (
              <div style={{ borderBottom: '1px solid ' + C.bd, marginBottom: 4 }}>
                <button onClick={() => setNotifOpen(v => !v)}
                  style={{ width: '100%', padding: '4px 12px 6px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Notify me for</span>
                  {!notifOpen && hasFilters && filterPills(activeTeam, activeSs, activeSvc)}
                  {!notifOpen && !hasFilters && <span style={{ fontSize: 9, color: C.dim2, flex: 1 }}>All</span>}
                  <span style={{ fontSize: 10, color: C.dim2, marginLeft: 'auto', transition: 'transform 0.2s', display: 'inline-block', transform: notifOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>
                {notifOpen && filterRows(notifTeam, setNotifTeamPref, notifSs, setNotifSsPref, notifSvc, setNotifSvcPref)}
              </div>
            )}
            {/* Notification divider when off */}
            {notifPerm !== 'granted' && <div style={{ borderBottom: '1px solid ' + C.bd, marginBottom: 4 }} />}
            {/* Copy Endorsement — copy button on top, collapsible filter below */}
            <div style={{ borderBottom: '1px solid ' + C.bd, marginBottom: 4 }}>
              {/* The copy button — always visible */}
              <button onClick={() => { setOpen(false); onPrint && onPrint(); }}
                style={{ width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderRadius: 7 }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>&#128203;</span>
                <span style={{ fontSize: 13, color: '#C8DFF0', textAlign: 'left' }}>Copy Endorsement</span>
              </button>
              {/* Collapsible filter header below */}
              <button onClick={() => setCopyOpen(v => !v)}
                style={{ width: '100%', padding: '2px 14px 6px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Copy filter</span>
                {!copyOpen && cpHasFilters && filterPills(cpActiveTeam, cpActiveSs, cpActiveSvc)}
                {!copyOpen && !cpHasFilters && <span style={{ fontSize: 9, color: C.dim2, flex: 1 }}>All</span>}
                <span style={{ fontSize: 10, color: C.dim2, marginLeft: 'auto', transition: 'transform 0.2s', display: 'inline-block', transform: copyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
              </button>
              {copyOpen && filterRows(copyTeam, setCopyTeamPref, copySs, setCopySsPref, copySvc, setCopySvcPref)}
            </div>
            {/* Start Rounds — button + collapsible filter */}
            <div style={{ borderBottom: '1px solid ' + C.bd, marginBottom: 4 }}>
              <button onClick={() => { setOpen(false); onWardRound && onWardRound(); }}
                style={{ width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderRadius: 7 }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>🔄</span>
                <span style={{ fontSize: 13, color: '#A8D878', textAlign: 'left' }}>Start Rounds</span>
              </button>
              <button onClick={() => setRoundOpen(v => !v)}
                style={{ width: '100%', padding: '2px 14px 6px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Round filter</span>
                {!roundOpen && rdHasFilters && filterPills(rdActiveTeam, rdActiveSs, rdActiveSvc)}
                {!roundOpen && !rdHasFilters && <span style={{ fontSize: 9, color: C.dim2, flex: 1 }}>All</span>}
                <span style={{ fontSize: 10, color: C.dim2, marginLeft: 'auto', transition: 'transform 0.2s', display: 'inline-block', transform: roundOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
              </button>
              {roundOpen && filterRows(roundTeam, setRoundTeamPref, roundSs, setRoundSsPref, roundSvc, setRoundSvcPref)}
            </div>
            <MenuItem icon="&#128190;" label="Copy Full Backup" color="#64D2FF" onClick={() => { setOpen(false); onBackup && onBackup(); }} />
            <MenuItem icon="&#128230;" label="Archive" color="#FF9500" onClick={() => { setOpen(false); onArchive && onArchive(); }} />
            <MenuItem icon="&#128202;" label="Import Patients" color="#64D2FF" onClick={() => { setOpen(false); onImport && onImport(); }} />
            <MenuItem icon="&#128221;" label="Audit Trail" color="#BF5AF2" onClick={() => { setOpen(false); onAudit(); }} />
            <div style={{ borderTop: '1px solid ' + C.bd, marginTop: 4, paddingTop: 4 }} />
            <MenuItem icon="&#8592;" label="Leave Database" color="#5A7A94" onClick={() => { setOpen(false); onLeave(); }} />
          </div>
        </>
      )}
    </div>
  );
}
function MenuItem({ icon, label, color, onClick }) {
  return (
    <button onClick={onClick} style={{ width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', color, fontSize: 13, cursor: 'pointer', textAlign: 'left', borderRadius: 7, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>{label}
    </button>
  );
}

// eslint-disable-next-line no-unused-vars
function SummaryFooter({ shiftCode, copied, copySum, onLeave, onAudit, notifPerm, onRequestNotif, onDisableNotif, teams, notifTeam, setNotifTeamPref, notifSs, setNotifSsPref, notifSvc, setNotifSvcPref, subspecs, copyTeam, setCopyTeamPref, copySs, setCopySsPref, copySvc, setCopySvcPref }) {
  const [copyOpen, setCopyOpen] = useState(false);
  const cpActiveTeam = teams?.find(t => t.id === copyTeam);
  const cpActiveSs   = subspecs?.find(s => s.id === copySs);
  const cpActiveSvc  = copySvc !== 'all' ? copySvc : null;
  const cpHasFilters = copyTeam !== 'all' || copySs !== 'all' || copySvc !== 'all';

  const pill = (color, label) => (
    <span style={{ fontSize: 10, fontWeight: 700, color, background: color + '22', border: '1px solid ' + color + '44', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{label}</span>
  );

  return (
    <div className="bottom-bar" style={{ borderTop: '1px solid ' + C.bd, background: C.bg0, flexShrink: 0 }}>
      {/* Copy Endorsement with collapsible filter */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: copyOpen ? 6 : 0 }}>
          <button onClick={copySum} style={{ flex: 1, padding: '8px 0', borderRadius: 10, background: copied ? '#081A0C' : C.bg2, border: '1px solid ' + (copied ? '#30D158' : C.bd), color: copied ? '#30D158' : '#C8DFF0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {copied ? '✓ Copied!' : ('📋 Copy' + (cpHasFilters ? ' filtered' : ' Endorsement'))}
          </button>
          {cpHasFilters && !copyOpen && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {cpActiveTeam && pill(cpActiveTeam.color, cpActiveTeam.name)}
              {cpActiveSs   && pill(cpActiveSs.color,   cpActiveSs.name)}
              {cpActiveSvc  && pill(CCOL[cpActiveSvc] || '#AAA', cpActiveSvc)}
            </div>
          )}
          <button onClick={() => setCopyOpen(v => !v)}
            style={{ padding: '6px 8px', borderRadius: 8, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 11, cursor: 'pointer', flexShrink: 0, transition: 'transform 0.2s', display: 'inline-block', transform: copyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</button>
        </div>
        {copyOpen && (
          <div style={{ background: C.bg2, borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {teams && teams.length > 0 && (
              <div>
                <div style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Team</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => (
                    <button key={t.id} onClick={() => setCopyTeamPref(t.id)}
                      style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((copyTeam || 'all') === t.id ? t.color : C.bd), background: (copyTeam || 'all') === t.id ? t.color + '22' : C.bg0, color: (copyTeam || 'all') === t.id ? t.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {subspecs?.length > 0 && (
              <div>
                <div style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Subspec</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...subspecs].map(s => (
                    <button key={s.id} onClick={() => setCopySsPref(s.id)}
                      style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((copySs || 'all') === s.id ? s.color : C.bd), background: (copySs || 'all') === s.id ? s.color + '22' : C.bg0, color: (copySs || 'all') === s.id ? s.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 9, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Service</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => (
                  <button key={opt.id} onClick={() => setCopySvcPref(opt.id)}
                    style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid ' + ((copySvc || 'all') === opt.id ? opt.color : C.bd), background: (copySvc || 'all') === opt.id ? opt.color + '22' : C.bg0, color: (copySvc || 'all') === opt.id ? opt.color : C.dim2, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <MoreMenu
          shiftCode={shiftCode}
          notifPerm={notifPerm} onRequestNotif={onRequestNotif} onDisableNotif={onDisableNotif}
          onAudit={onAudit} onLeave={onLeave}
          onPrint={copySum}
          teams={teams} notifTeam={notifTeam} setNotifTeamPref={setNotifTeamPref}
          notifSs={notifSs} setNotifSsPref={setNotifSsPref}
          notifSvc={notifSvc} setNotifSvcPref={setNotifSvcPref}
          subspecs={subspecs}
          copyTeam={copyTeam} setCopyTeamPref={setCopyTeamPref}
          copySs={copySs} setCopySsPref={setCopySsPref}
          copySvc={copySvc} setCopySvcPref={setCopySvcPref}
        />
      </div>
    </div>
  );
}

function ImportModal({ currentCode, onClose, onMergeHere }) {
  const [srcCode,  setSrcCode]  = useState('');
  const [srcPin,   setSrcPin]   = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [preview,  setPreview]  = useState(null); // { pts, teams, subspecs }
  const [done,     setDone]     = useState(false);

  const handleLookup = async () => {
    const code = srcCode.toUpperCase().trim();
    if (!code) { setError('Enter a database code.'); return; }
    if (code === currentCode) { setError("That's your current database."); return; }
    setLoading(true); setError('');
    try {
      const data = await loadShift(code);
      if (!data) { setError(`"${code}" not found. Check the code and try again.`); setLoading(false); return; }
      if (data.pinHash) {
        if (!srcPin) { setError('This database requires a PIN.'); setLoading(false); return; }
        if (btoa(srcPin) !== data.pinHash) { setError('Incorrect PIN.'); setLoading(false); return; }
      }
      setPreview({ pts: data.patients || [], teams: data.teams || [], subspecs: data.subspecs || [], code });
    } catch { setError('Could not connect. Check your internet connection.'); }
    setLoading(false);
  };

  const handleMerge = () => {
    onMergeHere(preview.pts, preview.teams, preview.subspecs);
    setDone(true);
  };

  const overlay = { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const sheet   = { width: '85%', maxWidth: 340, background: C.bg1, borderRadius: 20, padding: '20px 20px', boxShadow: '0 -8px 40px rgba(0,0,0,0.5)', border: `1px solid ${C.bd}` };
  const inp     = { width: '100%', background: C.bg3, border: `1.5px solid ${C.bd}`, borderRadius: 10, padding: '8px 12px', color: C.tx, fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', outline: 'none', textAlign: 'center', boxSizing: 'border-box' };
  const btn     = (bg, col, brd) => ({ width: '100%', padding: '11px 0', borderRadius: 12, background: bg, color: col, border: brd || 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 10, display: 'block', boxSizing: 'border-box' });

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={sheet}>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.tx, marginBottom: 4 }}>↓ Import Patients</div>
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 20 }}>
            Merge patients from another database into <span style={{ color: '#4A9EFF', fontWeight: 700 }}>{currentCode}</span>
          </div>
        </div>

        {done ? (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#30D158' }}>Merge complete!</div>
              <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>{preview.pts.length} patients imported into {currentCode}</div>
            </div>
            <button onClick={onClose} style={btn('#1A3D6A', '#7DC4FF')}>Done</button>
          </>
        ) : preview ? (
          <>
            <div style={{ background: C.bg2, borderRadius: 12, padding: '14px 16px', marginBottom: 20, border: `1px solid ${C.bd}` }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Importing from {preview.code}</div>
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#4A9EFF' }}>{preview.pts.length}</div>
                  <div style={{ fontSize: 11, color: C.dim }}>patients</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#4A9EFF' }}>{preview.teams.length}</div>
                  <div style={{ fontSize: 11, color: C.dim }}>teams</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#4A9EFF' }}>{preview.subspecs.length}</div>
                  <div style={{ fontSize: 11, color: C.dim }}>subspecs</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.dim2, marginTop: 10 }}>All patients, teams and subspecs will be merged into {currentCode}. Existing data is untouched.</div>
            </div>
            <button onClick={handleMerge} style={btn('#0D2A0D', '#30D158', '1.5px solid #30D158')}>✓ Confirm Merge</button>
            <button onClick={() => setPreview(null)} style={btn('transparent', C.dim, `1px solid ${C.bd}`)}>← Back</button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Source database code</div>
              <input value={srcCode} onChange={e => { setSrcCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 12)); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLookup()} placeholder="e.g. IMICU-2025" autoFocus style={inp} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>PIN</div>
              <input value={srcPin} onChange={e => { setSrcPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLookup()} placeholder="4 digits" maxLength={4} inputMode="numeric"
                style={{ ...inp, letterSpacing: '0.2em' }} />
            </div>
            {error && <div style={{ color: '#FF4545', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>{error}</div>}
            <button onClick={handleLookup} disabled={loading} style={btn('#1A3D6A', '#7DC4FF')}>{loading ? 'Looking up…' : '→ Look Up Database'}</button>
            <button onClick={onClose} style={btn('transparent', C.dim, `1px solid ${C.bd}`)}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [shiftCode,    setShiftCode]    = useState(() => localStorage.getItem('hpwa_code') || null);   // null = show ShiftGate
  const [pts,          setPts]          = useState([]);
  const [teams,        setTeams]        = useState(DTMS);
  const [subspecs,     setSubspecs]     = useState(DSUBSPECS);
  const [sync,         setSync]         = useState('loading');
  const [meta,         setMeta]         = useState(null);
  const [view,         setView]         = useState('list');
  const [selId,        setSelId]        = useState(null);
  const [tf,           setTf]           = useState('all');
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [summFiltersOpen, setSummFiltersOpen] = useState(false);
  const [search,       setSearch]       = useState('');
  const [tab,          setTab]          = useState('issues');
  const [copied,       setCopied]       = useState(false); // eslint-disable-line no-unused-vars
  const [copyTeam,     setCopyTeam]     = useState(() => localStorage.getItem('copyTeam') || 'all');
  const [copySs,       setCopySs]       = useState(() => localStorage.getItem('copySs')   || 'all');
  const [copySvc,      setCopySvc]      = useState(() => localStorage.getItem('copySvc')  || 'all');
  const setCopyTeamPref = v => { setCopyTeam(v); localStorage.setItem('copyTeam', v); };
  const setCopySsPref   = v => { setCopySs(v);   localStorage.setItem('copySs',   v); };
  const setCopySvcPref  = v => { setCopySvc(v);  localStorage.setItem('copySvc',  v); };
  const [roundTeam,    setRoundTeam]    = useState(() => localStorage.getItem('roundTeam') || 'all');
  const [roundSs,      setRoundSs]      = useState(() => localStorage.getItem('roundSs')   || 'all');
  const [roundSvc,     setRoundSvc]     = useState(() => localStorage.getItem('roundSvc')  || 'all');
  const setRoundTeamPref = v => { setRoundTeam(v); localStorage.setItem('roundTeam', v); };
  const setRoundSsPref   = v => { setRoundSs(v);   localStorage.setItem('roundSs',   v); };
  const setRoundSvcPref  = v => { setRoundSvc(v);  localStorage.setItem('roundSvc',  v); };
  const [backedUp,     setBackedUp]     = useState(false); // eslint-disable-line no-unused-vars
  const [newPt,        setNewPt]        = useState(() => blankPt());
  const [showTM,       setShowTM]       = useState(false);
  const [newTN,        setNewTN]        = useState('');
  const [vd,           setVd]           = useState(() => newVD());
  const [ldate,        setLdate]        = useState(() => toDay());
  const [ltime,        setLtime]        = useState(() => toTime());
  const [dragIdx,      setDragIdx]      = useState(null);
  const [dragOver,     setDragOver]     = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [expandGroup,  setExpandGroup]  = useState(null); // 'status'|'resus'|'subspec'
  const [showArchive,  setShowArchive]  = useState(false);
  const [showImport,   setShowImport]   = useState(false);
  const [dispFilter,   setDispFilter]   = useState('all');  // disposition/urgency filter
  const [userName,       setUserName]      = useState(() => localStorage.getItem('hpwa_user') || '');
  const [showAudit,      setShowAudit]     = useState(false);
  const [showFaq,        setShowFaq]       = useState(false);
  const [notifPerm,      setNotifPerm]     = useState(() => {
    if (typeof Notification === 'undefined') return 'default';
    if (localStorage.getItem('notifPerm') === 'disabled') return 'disabled';
    return Notification.permission;
  });
  const [svcFilter,    setSvcFilter]    = useState('all');  // service filter
  const [ssFilter,     setSsFilter]     = useState('all');  // subspec filter
  const [summSsFilter, setSummSsFilter] = useState('all');
  const [showSM,       setShowSM]       = useState(false);  // subspec modal
  const [newSSN,       setNewSSN]       = useState('');
  const [summDisp,     setSummDisp]     = useState('all');  // summary disposition filter
  const [summSvc,      setSummSvc]      = useState('all');  // summary service filter
  const [addingTask,   setAddingTask]   = useState(null);   // ptId being quick-added to
  const [newTaskText,  setNewTaskText]  = useState('');
  const [newTaskType,  setNewTaskType]  = useState('general');
  const [viewMode, setViewMode]  = useState(() => localStorage.getItem('viewMode') || 'mobile');
  const setVM = m => { setViewMode(m); localStorage.setItem('viewMode', m); };
  const [denseMode,    setDenseMode]    = useState(() => localStorage.getItem('denseMode') === 'true');
  const setDense = v => { setDenseMode(v); localStorage.setItem('denseMode', String(v)); };
  const [notifTeam,    setNotifTeam]    = useState(() => localStorage.getItem('notifTeam') || 'all');
  const [notifSs,      setNotifSs]      = useState(() => localStorage.getItem('notifSs')   || 'all');
  const [notifSvc,     setNotifSvc]     = useState(() => localStorage.getItem('notifSvc')  || 'all');
  const setNotifTeamPref = v => { setNotifTeam(v); localStorage.setItem('notifTeam', v); syncPrefsToSW({ team: v, ss: notifSs, svc: notifSvc, notifDisabled: notifPerm === 'disabled' }); };
  const setNotifSsPref   = v => { setNotifSs(v);   localStorage.setItem('notifSs',   v); syncPrefsToSW({ team: notifTeam, ss: v, svc: notifSvc, notifDisabled: notifPerm === 'disabled' }); };
  const setNotifSvcPref  = v => { setNotifSvc(v);  localStorage.setItem('notifSvc',  v); syncPrefsToSW({ team: notifTeam, ss: notifSs, svc: v, notifDisabled: notifPerm === 'disabled' }); };
  const [showAllTasks,     setShowAllTasks]     = useState(false);
  const [atTeamFilter,     setAtTeamFilter]     = useState('all');
  const [atSsFilter,       setAtSsFilter]       = useState('all');
  const [atSvcFilter,      setAtSvcFilter]      = useState('all');
  const [atShowDone,       setAtShowDone]       = useState(false);
  const [showWardRound,setShowWardRound]= useState(false);
  const [wardRoundIdx, setWardRoundIdx]  = useState(0);
  const [lastLoginAt]  = useState(() => {
    const stored = localStorage.getItem('hpwa_lastlogin');
    const now = Date.now();
    localStorage.setItem('hpwa_lastlogin', String(now));
    return stored ? parseInt(stored, 10) : now;
  });
  const timer    = useRef(null);
  const isMine   = useRef(0);  // timestamp of last local write — suppress echoes for 2s
  const sidebarScrollRef = useRef(null); // preserve desktop sidebar scroll position
  const prevPtsRef = useRef([]);  // track previous pts for referral change detection

  const cur     = pts.find(p => p.id === selId) || null;
  const getTeam = id => teams.find(t => t.id === id);
  const preDisp = [...pts]
    .filter(p => tf === 'all' || p.team === tf)
    .filter(p => svcFilter  === 'all' || p.code    === svcFilter)
    .filter(p => ssFilter   === 'all' || p.subspec  === ssFilter)
    .filter(p => { const q = search.toLowerCase(); return !q || p.name.toLowerCase().includes(q) || (p.mrn && p.mrn.includes(q)) || (p.room && p.room.toLowerCase().includes(q)) || (p.consultant && p.consultant.toLowerCase().includes(q)); });
  const filtered = preDisp
    .filter(p => dispFilter === 'all' || p.urgency === dispFilter)
    .sort((a, b) => (a.room || '').localeCompare(b.room || '', undefined, { numeric: true, sensitivity: 'base' }));

  // ── JOIN SHIFT (called by ShiftGate) ──
  const handleJoin = useCallback((code, initialPatients, initialTeams, name, initialSubspecs) => {
    setActiveShift(code, initialTeams);
    setShiftCode(code);
    localStorage.setItem('hpwa_code', code);
    setPts((initialPatients || []).map(migrate));
    setTeams(initialTeams || DTMS);
    setSubspecs(initialSubspecs && initialSubspecs.length ? initialSubspecs : DSUBSPECS);
    setMeta({ saved: new Date().toISOString(), count: (initialPatients || []).length });
    setSync('synced');
    if (name) { setUserName(name); localStorage.setItem('hpwa_user', name); }
    if (localStorage.getItem('fcm_subscription')) registerDeviceForShift(code);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── MIDNIGHT ARCHIVE CHECK ──
  // ── VIEWPORT CHANGE (foldables, orientation) ──
  useEffect(() => {
    // Force re-render on orientation change or fold/unfold so safe-area CSS vars update
    const onResize = () => { document.documentElement.style.setProperty('--vw', window.innerWidth + 'px'); };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onResize); };
  }, []);

  // ── MIDNIGHT ARCHIVE ──
  useEffect(() => {
    if (!shiftCode) return;
    if (!shouldRunMidnightArchive()) return;
    // Check if it's past midnight (between 00:00 and 00:05 to avoid running all day)
    // Run if we haven't run today (any time after midnight we first load)
    const { remaining, archived } = archiveDischarged(pts, shiftCode);
    if (archived > 0) {
      save(remaining);
      markArchiveRan();
      alert(`${archived} discharged patient${archived !== 1 ? 's' : ''} have been moved to the archive.`);
    } else {
      markArchiveRan();
    }
  }, [shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── REAL-TIME LISTENER (Firebase — per-patient subcollection) ──
  // Auto-load when returning user has a saved code (skip ShiftGate)
  useEffect(() => {
    const savedCode = localStorage.getItem('hpwa_code');
    const savedName = localStorage.getItem('hpwa_user');
    if (!savedCode || !isFirebaseConfigured()) return;
    loadShift(savedCode).then(data => {
      if (!data) { localStorage.removeItem('hpwa_code'); setShiftCode(null); return; }
      setActiveShift(savedCode, data.teams || DTMS);
      setPts((data.patients || []).map(migrate));
      setTeams(data.teams || DTMS);
      setSubspecs(data.subspecs?.length ? data.subspecs : DSUBSPECS);
      setMeta({ saved: new Date().toISOString(), count: (data.patients || []).length });
      setSync('synced');
      if (savedName) setUserName(savedName);
      registerDeviceForShift(savedCode);
    }).catch(() => { localStorage.removeItem('hpwa_code'); setShiftCode(null); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shiftCode || !isFirebaseConfigured()) return;

    // Run migration in background (old flat-array → subcollection)
    migrateIfNeeded(shiftCode);

    const unsub = subscribePatients(
      shiftCode,
      (type, patient) => {
        // Skip echo from our own writes (suppress for 2s after last local save)
        if (Date.now() - isMine.current < 2000) return;
        if (type === 'added' || type === 'modified') {
          setPts(prev => {
            const exists = prev.some(p => p.id === patient.id);
            if (exists) return prev.map(p => p.id === patient.id ? migrate(patient) : p);
            return [...prev, migrate(patient)];
          });
        } else if (type === 'removed') {
          setPts(prev => prev.filter(p => p.id !== patient.id));
        }
        setSync('synced');
      },
      () => setSync('error')
    );
    return () => unsub();
  }, [shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry failed saves — flushes the offline/error queue back to Firebase
  const load = useCallback(async () => {
    try {
      setSync('saving');
      // flushQueue replays everything in localStorage queue back to Firebase
      await flushQueue();
      // Also re-save via setPts so the save() path runs for current in-memory state
      setPts(current => {
        if (current.length) {
          setTimeout(async () => {
            try {
              for (const p of current) await stor.saveOne(p);
              setMeta({ saved: new Date().toISOString(), count: current.length });
              setSync('synced');
            } catch {
              setSync('error');
            }
          }, 0);
        } else {
          setSync('synced');
        }
        return current;
      });
    } catch {
      setSync('error');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SAVE ──
  // save(updated) — diffs against current pts and writes only changed/added patients
  const save = useCallback((updated) => {
    setPts(prev => {
      // Compute which patients changed or were added
      const changed = updated.filter(p => {
        const old = prev.find(o => o.id === p.id);
        return !old || JSON.stringify(old) !== JSON.stringify(p);
      });
      const deleted = prev.filter(p => !updated.some(u => u.id === p.id));

      if (changed.length || deleted.length) {
        setSync('saving');
        isMine.current = Date.now();
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
          try {
            // Write each changed patient individually
            for (const p of changed) await stor.saveOne(p);
            // Delete removed patients
            for (const p of deleted) await stor.deleteOne(p.id);
            setMeta({ saved: new Date().toISOString(), count: updated.length });
            setSync('synced');
          } catch {
            setSync('error');
            isMine.current = 0;
          }
        }, 400);
      }
      return updated;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTeams = useCallback(async (t) => {
    setTeams(t);
    isMine.current = Date.now();
    await stor.saveTeams(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSubspecs = useCallback(async (ss) => {
    setSubspecs(ss);
    isMine.current = Date.now();
    await stor.saveSubspecs(ss);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addSubspec = () => {
    if (!newSSN.trim()) return;
    const ss = { id: `ss${Date.now()}`, name: newSSN.trim(), color: SSCOLS[subspecs.length % SSCOLS.length] };
    saveSubspecs([...subspecs, ss]); setNewSSN('');
  };
  const delSubspec = id => { saveSubspecs(subspecs.filter(s => s.id !== id)); if (ssFilter === id) setSsFilter('all'); if (summSsFilter === id) setSummSsFilter('all'); };

  const setFld = (f, v) => {
    const cur2 = pts.find(p => p.id === selId);
    save(pts.map(p => p.id === selId ? { ...p, [f]: v } : p));
    if (!cur2) return;
    if (f === 'urgency' && v !== cur2.urgency) {
      audit('Status changed', cur2.name, (cur2.urgency || '') + ' -> ' + v, selId);
      const urgencyLabels = { critical: 'CRITICAL', new: 'New Admission', stable: 'Stable', mgh: 'MGH', discharged: 'Discharged', mortality: 'Mortality' };
      if (v === 'mortality') {
        sendPushNotif('💀 Mortality', (cur2.name || 'Unknown') + (cur2.room ? ' · Rm ' + cur2.room : '') + ' — patient deceased', cur2);
      } else if (v === 'discharged') {
        sendPushNotif('🚪 Patient Discharged', (cur2.name || 'Unknown') + (cur2.room ? ' · Rm ' + cur2.room : '') + ' — discharged', cur2);
      } else {
        sendPushNotif('⚠️ Status Change', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' → ' + (urgencyLabels[v] || v), cur2);
      }
    } else if (f === 'resus') {
      audit('Resus changed', cur2.name, 'Resus: ' + v, selId);
    }
  };
  const patch      = fn      => save(pts.map(p => p.id === selId ? { ...fn(p), lastModified: Date.now() } : p));
  const patchById  = (id, fn) => save(pts.map(p => p.id === id   ? { ...fn(p), lastModified: Date.now() } : p));

  // ── SHOW SHIFT GATE if not in a shift yet ──
  // ── Audit logging helper — must be before early returns ─────────────────
  const audit = useCallback((action, patientName, detail, patientId) => {
    if (!shiftCode) return;
    logAudit(shiftCode, { action, patientName: patientName || '', patientId: patientId || '', detail: detail || '', by: userName || 'Unknown' });
  }, [shiftCode, userName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Push notification helper ──────────────────────────────────────────────
  const sendPushNotif = useCallback((title, body, pt) => {
    // ── Cross-device only: OneSignal sends to all OTHER devices in the database ──
    // The sender is excluded in push.js by comparing deviceId
    if (shiftCode) sendPush(shiftCode, title, body, pt || {});
  }, [notifPerm, shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveReferral = useCallback((entry) => {
    const cur2 = pts.find(p => p.id === selId);
    if (!cur2) return;
    patch(p => ({ ...p, referLog: [...(p.referLog || []), entry] }));
    if (shiftCode) sendPush(shiftCode, '📋 New Referral', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' → ' + entry.service + ' (' + entry.status + ')', cur2);
  }, [pts, selId, shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeReferStatus = useCallback((id, newStatus) => {
    const cur2 = pts.find(p => p.id === selId);
    if (!cur2) return;
    const entry = (cur2.referLog || []).find(e => e.id === id);
    if (!entry || entry.status === newStatus) return;
    patch(p => ({ ...p, referLog: (p.referLog || []).map(e => e.id === id ? { ...e, status: newStatus } : e) }));
    if (shiftCode) sendPush(shiftCode, '🔄 Referral Update', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + entry.service + ': ' + newStatus, cur2);
  }, [pts, selId, shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestNotifPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    localStorage.removeItem('notifPerm'); // clear disabled flag
    setNotifPerm(perm);
    if (perm === 'granted') {
      const sub = await requestPushPermission();
      if (sub && shiftCode) registerDeviceForShift(shiftCode);
    }
  }, [shiftCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const disableNotifications = useCallback(() => {
    localStorage.setItem('notifPerm', 'disabled'); // persist across reloads
    setNotifPerm('disabled');
    syncPrefsToSW({ team: localStorage.getItem('notifTeam') || 'all', ss: localStorage.getItem('notifSs') || 'all', svc: localStorage.getItem('notifSvc') || 'all', notifDisabled: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markReviewed = useCallback((ptId) => {
    save(pts.map(p => p.id === ptId ? { ...p, lastReviewed: Date.now() } : p));
  }, [pts]); // eslint-disable-line react-hooks/exhaustive-deps

  const consultNotify = useCallback((pt) => {
    const ptTeam = teams.find(t => t.id === pt.team);
    // Latest vitals
    const latestVitals = [...(pt.vitalsLog || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))[0];
    const vitalsLine = latestVitals ? [
      latestVitals.vBP  ? 'BP: ' + latestVitals.vBP : '',
      latestVitals.vHR  ? 'HR: ' + latestVitals.vHR : '',
      latestVitals.vRR  ? 'RR: ' + latestVitals.vRR : '',
      latestVitals.vT   ? 'T: ' + latestVitals.vT + '\u00b0C' : '',
      latestVitals.vO2  ? 'SpO2: ' + latestVitals.vO2 + '%' : '',
      latestVitals.vAVPU ? 'AVPU: ' + latestVitals.vAVPU : '',
      (latestVitals.vGCSe && latestVitals.vGCSv && latestVitals.vGCSm) ? 'GCS: ' + (parseInt(latestVitals.vGCSe) + parseInt(latestVitals.vGCSv) + parseInt(latestVitals.vGCSm)) + ' E' + latestVitals.vGCSe + 'V' + latestVitals.vGCSv + 'M' + latestVitals.vGCSm : '',
    ].filter(Boolean).join(' | ') : '';
    // Active meds
    const activeMeds = (pt.medsList || []).filter(m => !m.dc).slice(0, 5).map(m => '- ' + [m.name, m.dose, m.freq, m.route].filter(Boolean).join(' ')).join('\n');
    const msg = [
      'PATIENT UPDATE',
      '---------------',
      'Patient: ' + (pt.name || 'Unknown'),
      'Room: ' + (pt.room || '?'),
      'Age/Sex: ' + (pt.age || '?') + (pt.sex || ''),
      ptTeam ? 'Team: ' + ptTeam.name : '',
      'Diagnosis: ' + (pt.diagnosis || '?'),
      '',
      vitalsLine ? 'Latest Vitals:\n' + vitalsLine : '',
      activeMeds ? 'Active Medications:\n' + activeMeds : '',
      '',
      'Active Issues:',
      (pt.issuesList || []).filter(i => !i.resolved).slice(0, 3).map(i => '- ' + (i.text || i)).join('\n') || (pt.activeIssues || 'See chart'),
      '',
      'Pending Tasks:',
      (pt.todos || []).filter(t => !t.done).slice(0, 3).map(t => '- ' + (t.text || 'Task')).join('\n') || 'None',
    ].filter(l => l !== undefined && l !== '').join('\n');
    if (navigator.share) {
      navigator.share({ text: msg }).catch(() => {});
    } else {
      navigator.clipboard.writeText(msg).then(() => alert('Update copied to clipboard')).catch(() => alert(msg));
    }
  }, [teams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-flush offline queue on mount
  useEffect(() => { if (navigator.onLine) flushQueue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    initPush().then(async () => {
      // Sync current prefs to SW cache so it can filter notifications
      syncPrefsToSW({
        team: localStorage.getItem('notifTeam') || 'all',
        ss:   localStorage.getItem('notifSs')   || 'all',
        svc:  localStorage.getItem('notifSvc')  || 'all',
        notifDisabled: localStorage.getItem('notifPerm') === 'disabled',
      });
      // If permission already granted, subscribe and register automatically
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const sub = await requestPushPermission();
        const code = localStorage.getItem('hpwa_code');
        if (sub && code) registerDeviceForShift(code);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watch pts for referLog changes and fire notifications ──────────────────
  useEffect(() => {
    const prev = prevPtsRef.current;
    if (prev.length > 0) {
      pts.forEach(pt => {
        const prevPt = prev.find(p => p.id === pt.id);
        const newLog = pt.referLog || [];
        const oldLog = prevPt?.referLog || [];
        // New referral added
        if (newLog.length > oldLog.length) {
          const added = newLog.find(e => !oldLog.some(o => o.id === e.id));
          if (added) sendPushNotif('📋 New Referral', (pt.name||'Patient') + (pt.room ? ' · Rm '+pt.room : '') + ' → ' + added.service + ' (' + added.status + ')', pt);
        }
        // Status changed on existing referral
        newLog.forEach(entry => {
          const old = oldLog.find(o => o.id === entry.id);
          if (old && old.status !== entry.status) {
            sendPushNotif('🔄 Referral Update', (pt.name||'Patient') + (pt.room ? ' · Rm '+pt.room : '') + ' — ' + entry.service + ': ' + entry.status, pt);
          }
        });
      });
    }
    prevPtsRef.current = pts;
  }, [pts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shiftCode) return <ShiftGate onJoin={handleJoin} />;


  // ── TODOS ──
  const addTodo  = (type='general', priority='routine') => {
    const cur2 = pts.find(p => p.id === selId);
    patch(p => ({ ...p, todos: [...(p.todos || []), { id: 't'+Date.now(), text: '', done: false, type, priority, status: 'Pending', order: (p.todos || []).length, addedAt: Date.now() }] }));
    if (cur2) {
      audit('Task added', cur2.name, type + ' task', selId);
      const priorityLabel = priority === 'urgent' ? '🚨' : priority === 'soon' ? '🔔' : '📋';
      const priorityText  = priority === 'urgent' ? 'Urgent' : priority === 'soon' ? 'Soon' : 'Routine';
      sendPushNotif(priorityLabel + ' Task (' + priorityText + ')', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + priorityText.toLowerCase() + ' ' + type + ' task added', cur2);
    }
  };
  const togTodo  = (id, extra={}) => {
    const cur2 = pts.find(p => p.id === selId);
    const task = (cur2?.todos || []).find(t => t.id === id);
    patch(p => ({ ...p, todos: p.todos.map(t => t.id === id ? { ...t, ...extra, done: extra.status !== undefined ? extra.status === 'Done' : t.done } : t) }));
    if (cur2 && task) {
      if (extra.priority && extra.priority !== task.priority) {
        const label = extra.priority === 'urgent' ? '🚨' : extra.priority === 'soon' ? '🔔' : '📋';
        const text  = extra.priority === 'urgent' ? 'Urgent' : extra.priority === 'soon' ? 'Soon' : 'Routine';
        sendPushNotif(label + ' Task Priority Changed', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + (task.text||'task') + ' → ' + text, cur2);
      } else if (extra.status && extra.status !== task.status) {
        const icon = extra.status === 'Done' ? '✅' : extra.status === 'In Progress' ? '🔄' : '⏳';
        sendPushNotif(icon + ' Task Updated', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + (task.text||'task') + ': ' + extra.status, cur2);
      }
    }
  };
  const toggleTaskForPt = (ptId, taskId, newStatus) => {
    const pt = pts.find(p => p.id === ptId);
    const task = (pt?.todos || []).find(t => t.id === taskId);
    save(pts.map(p => p.id !== ptId ? p : { ...p, todos: (p.todos || []).map(t => t.id !== taskId ? t : { ...t, status: newStatus, done: newStatus === 'Done' }) }));
    if (pt && task && newStatus !== task.status) {
      const icon = newStatus === 'Done' ? '✅' : newStatus === 'In Progress' ? '🔄' : '⏳';
      sendPushNotif(icon + ' Task Updated', (pt.name||'Patient') + (pt.room ? ' · Rm '+pt.room : '') + ' — ' + (task.text||'task') + ': ' + newStatus, pt);
    }
  };
  const editTodo = (id, txt)   => patch(p => ({ ...p, todos: p.todos.map(t => t.id === id ? { ...t, text: txt } : t) }));
  const delTodo  = id          => patch(p => ({ ...p, todos: p.todos.filter(t => t.id !== id) }));
  const moveTodo = (sorted, idx, dir) => {
    const arr = [...sorted], tgt = idx + dir;
    if (tgt < 0 || tgt >= arr.length) return;
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    patch(p => ({ ...p, todos: arr.map((t, i) => ({ ...t, order: i })) }));
  };
  const dropTodo = (idx, sorted) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOver(null); return; }
    const arr = [...sorted]; const [m] = arr.splice(dragIdx, 1); arr.splice(idx, 0, m);
    patch(p => ({ ...p, todos: arr.map((t, i) => ({ ...t, order: i })) }));
    setDragIdx(null); setDragOver(null);
  };

  // ── VITALS ──
  const saveVitals = () => {
    const hasGCS = vd.vGCSe && vd.vGCSv && vd.vGCSm;
    if (!['vBP','vHR','vRR','vT','vO2','vAVPU'].some(k => vd[k] && vd[k] !== 'A') && !hasGCS) return;
    if (!['vBP','vHR','vRR','vT','vO2'].some(k => vd[k]) && !hasGCS) return;
    const cur2 = pts.find(p => p.id === selId);
    patch(p => ({ ...p, vitalsLog: [...(p.vitalsLog || []), { id: 'v'+Date.now(), ...vd }] }));
    const gcsStr = hasGCS ? ' GCS:' + (parseInt(vd.vGCSe)+parseInt(vd.vGCSv)+parseInt(vd.vGCSm)) + '(E'+vd.vGCSe+'V'+vd.vGCSv+'M'+vd.vGCSm+')' : '';
    if (cur2) {
      audit('Vitals saved', cur2.name, 'BP:' + (vd.vBP||'-') + ' HR:' + (vd.vHR||'-') + ' RR:' + (vd.vRR||'-') + ' T:' + (vd.vT||'-') + ' SpO2:' + (vd.vO2||'-') + gcsStr, selId);
      const gcs2 = (vd.vGCSe && vd.vGCSv && vd.vGCSm) ? ' GCS:' + (parseInt(vd.vGCSe)+parseInt(vd.vGCSv)+parseInt(vd.vGCSm)) : '';
      const vitBody = [
        'BP:' + (vd.vBP||'-'),
        'HR:' + (vd.vHR||'-'),
        'RR:' + (vd.vRR||'-'),
        'T:' + (vd.vT||'-'),
        'SpO2:' + (vd.vO2||'-'),
        vd.vAVPU ? 'AVPU:' + vd.vAVPU : '',
        gcs2,
      ].filter(Boolean).join(' ');
      sendPushNotif('📊 Vitals', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + vitBody, cur2);
    }
    setVd(newVD());
  };
  const delVitals = id => { if (!window.confirm('Delete this vitals entry?')) return; patch(p => ({ ...p, vitalsLog: (p.vitalsLog || []).filter(v => v.id !== id) })); };

  // ── LABS ──
  const addLabSet  = (pre = '', batchEntries = null, bdate = null, btime = null) => {
    const cur2 = pts.find(p => p.id === selId);
    if (pre === '__batch__' && batchEntries) {
      patch(p => ({ ...p, labsLog: [...(p.labsLog || []), { id: `g${Date.now()}`, date: bdate || ldate, time: btime || ltime, entries: batchEntries.map(e => ({ ...e, id: `e${Date.now()}-${Math.random()}` })) }] }));
    } else {
      patch(p => ({ ...p, labsLog: [...(p.labsLog || []), { id: `g${Date.now()}`, date: ldate, time: ltime, entries: pre ? [{ id: `e${Date.now()}`, name: pre, value: '', unit: '', flag: '' }] : [] }] }));
    }
    if (cur2) {
      const testNames = batchEntries ? batchEntries.map(e => e.name).filter(Boolean).slice(0,4).join(', ') : (pre || 'new panel');
      sendPushNotif('🧪 New Labs', (cur2.name||'Patient') + (cur2.room ? ' · Rm '+cur2.room : '') + ' — ' + testNames, cur2);
    }
  };
  const addLabRow  = gid        => patch(p => ({ ...p, labsLog: (p.labsLog || []).map(g => g.id === gid ? { ...g, entries: [...g.entries, { id: `e${Date.now()}`, name: '', value: '', unit: '', flag: '' }] } : g) }));
  const editLab    = (gid, eid, f, v) => patch(p => ({ ...p, labsLog: (p.labsLog || []).map(g => g.id === gid ? { ...g, entries: g.entries.map(e => e.id === eid ? { ...e, [f]: v } : e) } : g) }));
  const delLabRow  = (gid, eid) => patch(p => ({ ...p, labsLog: (p.labsLog || []).map(g => g.id === gid ? { ...g, entries: g.entries.filter(e => e.id !== eid) } : g) }));
  const delLabSet  = gid        => patch(p => ({ ...p, labsLog: (p.labsLog || []).filter(g => g.id !== gid) }));
  const saveLabSet = (gid, entries, date, time) => patch(p => ({ ...p, labsLog: (p.labsLog || []).map(g => g.id === gid ? { ...g, date, time, entries } : g) }));
  const clearLabFlag = (gid, eid) => patch(p => ({ ...p, labsLog: (p.labsLog || []).map(g => g.id === gid ? { ...g, entries: g.entries.map(e => e.id === eid ? { ...e, flag: '' } : e) } : g) }));

  // ── FLUID I/O ──
  const getIODay = date => (cur?.ioLog || []).find(d => d.date === date) || { id: null, date, inputs: [], outputs: [] };
  const upsertIODay = (date, fn) => patch(p => {
    const log = [...(p.ioLog || [])];
    const idx = log.findIndex(d => d.date === date);
    const day = idx >= 0 ? log[idx] : { id: `io${Date.now()}`, date, inputs: [], outputs: [] };
    const updated = fn(day);
    if (idx >= 0) log[idx] = updated; else log.push(updated);
    return { ...p, ioLog: log };
  });
  const addIOEntry  = (date, type)          => upsertIODay(date, day => ({ ...day, [type]: [...day[type], { id: `e${Date.now()}`, label: '', amount: '' }] }));
  const editIOEntry = (date, type, eid, f, v) => upsertIODay(date, day => ({ ...day, [type]: day[type].map(e => e.id === eid ? { ...e, [f]: v } : e) }));
  const delIOEntry  = (date, type, eid)     => upsertIODay(date, day => ({ ...day, [type]: day[type].filter(e => e.id !== eid) }));
  const delIODay    = date                  => patch(p => ({ ...p, ioLog: (p.ioLog || []).filter(d => d.date !== date) }));

  // ── TEAMS / PATIENTS ──
  const addTeam = () => {
    if (!newTN.trim()) return;
    const t = { id: `tc${Date.now()}`, name: newTN.trim(), color: TCOLS[teams.length % TCOLS.length], icon: newTN.trim()[0].toUpperCase() };
    saveTeams([...teams, t]); setNewTN('');
  };
  const delTeam = id => { saveTeams(teams.filter(t => t.id !== id)); if (tf === id) setTf('all'); };
  const delPt   = id => { save(pts.filter(p => p.id !== id)); setView('list'); };
  const manualArchive = (patient) => {
    archivePatient(patient, shiftCode).then(() => delPt(patient.id));
  };
  const addPt   = () => {
    const p = { ...newPt, id: 'p'+Date.now(), resus: newPt.resus || 'full' };
    // Duplicate detection
    const dupMrn  = newPt.mrn  && pts.find(x => x.mrn  === newPt.mrn);
    const dupName = newPt.name && newPt.dob && pts.find(x => x.name === newPt.name && x.dob === newPt.dob);
    if (dupMrn) {
      if (!window.confirm('A patient with MRN ' + newPt.mrn + ' already exists (' + dupMrn.name + '). Add anyway?')) return;
    } else if (dupName) {
      if (!window.confirm('A patient named ' + newPt.name + ' with same DOB already exists. Add anyway?')) return;
    }
    save([...pts, p]);
    audit('Patient added', p.name || 'Unnamed', p.diagnosis || '', p.id);
    sendPushNotif('🆕 New Patient', (p.name || 'Unnamed') + (p.room ? ' · Rm ' + p.room : '') + (p.diagnosis ? ' — ' + p.diagnosis : ''), p);
    setSelId(p.id); setTab('issues'); setView('detail');
  };

  // ── SUMMARY ──
  const makeSummary = (overridePts) => {
    const pool = overridePts
      ? overridePts
      : pts
          .filter(p => p.urgency !== 'discharged')
          .filter(p => copyTeam === 'all' || p.team    === copyTeam)
          .filter(p => copySs   === 'all' || p.subspec === copySs)
          .filter(p => copySvc  === 'all' || p.code    === copySvc);
    const teamLabel = copyTeam !== 'all' ? (teams.find(t => t.id === copyTeam)?.name || copyTeam)
                    : copySs   !== 'all' ? (subspecs.find(s => s.id === copySs)?.name || copySs)
                    : copySvc  !== 'all' ? copySvc
                    : (tf === 'all' ? 'All Teams' : (getTeam(tf)?.name || ''));
    const now = new Date();
    return `ENDORSEMENTS \u2014 ${teamLabel}\n${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}\n${'='.repeat(50)}\n\n` +
      [...pool].sort((a, b) => UORD[a.urgency] - UORD[b.urgency]).map(p => {
        const pend     = (p.todos         || []).filter(t => !t.done && t.status !== 'Done');
        const diet     = (p.dietList      || []).filter(d => !d.dc);
        const dcDiet   = (p.dietList      || []).filter(d => d.dc);
        const procs    = (p.procedureList || []);
        const dietStr  = diet.length
          ? '\nDiet: ' + diet.map(d => d.order + (d.note ? ' (' + d.note + ')' : '')).join(', ')
          : '';
        const dcDietStr = dcDiet.length
          ? '\nDC Diet: ' + dcDiet.map(d => d.order + (d.note ? ' (' + d.note + ')' : '') + (d.dcNote ? ' - ' + d.dcNote : '')).join(', ')
          : '';
        const procStr  = procs.length
          ? '\nProcedures:\n' + procs.map(pr =>
              '  - ' + pr.name + (pr.date ? ' [' + pr.date + ']' : '') + ' | ' + pr.status + (pr.physician ? ' | ' + pr.physician : '') + (pr.note ? ' - ' + pr.note : '')
            ).join('\n')
          : '';
        const activeMeds = (p.medsList || []).filter(m => !m.dc);
        const abxDayNum  = sd => sd ? Math.max(1, Math.floor((Date.now() - new Date(sd)) / 86400000) + 1) : null;
        const medsStr    = activeMeds.length
          ? '\nMeds: ' + activeMeds.map(m => {
              let s = m.name + (m.dose ? ' ' + m.dose : '') + (m.route ? ' ' + m.route : '') + (m.freq ? ' ' + m.freq : '');
              if (m.startDate) {
                const d = abxDayNum(m.startDate);
                s += ' (started ' + m.startDate + ' D' + d + (m.abxTotal ? '/' + m.abxTotal : '') + ')';
              }
              return s;
            }).join(', ')
          : '';
        const changes24 = get24hChanges(p);
        const ch24Str = changes24.length
          ? '\nLast 24h:\n' + changes24.map(ch => '  - ' + ch.label + ': ' + ch.text).join('\n')
          : '';
        return '[' + URG[p.urgency].label + '] ' + p.name + ' | Rm ' + p.room + ' | ' + p.age + p.sex + (p.weight ? ' | ' + p.weight + (p.weightUnit || 'kg') : '') + ' | ' + p.code + (p.consultant ? ' | ' + p.consultant : '') + '\nMRN: ' + p.mrn + '\nDx: ' + (p.diagnosis || '-') + '\n' + (p.activeIssues || '') + (pend.length ? '\nPending:\n' + pend.map(t => '  - ' + t.text).join('\n') : '') + medsStr + dietStr + dcDietStr + procStr + ch24Str + (p.note ? '\nNote: ' + p.note : '');
      }).join('\n\n' + '-'.repeat(44) + '\n');
  }
  // ── 24h activity feed for a patient ─────────────────────────────────────
  const get24hChanges = (p) => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const ago24 = new Date(cutoff);
    const items = [];
    // New issues
    (p.issuesList || []).forEach(i => {
      if (i.date && new Date(i.date) >= ago24 && i.text && i.text.trim())
        items.push({ label: 'New issue', text: i.text.trim(), color: '#FF6B6B' });
    });
    // Resolved issues
    (p.resolvedIssues || []).forEach(i => {
      if (i.resolvedDate && new Date(i.resolvedDate) >= ago24 && i.text && i.text.trim())
        items.push({ label: 'Resolved', text: i.text.trim(), color: '#30D158' });
    });
    // New meds
    (p.medsList || []).filter(m => !m.dc && m.addedAt && m.addedAt >= cutoff && m.name && m.name.trim()).forEach(m => {
      const parts = [m.name.trim(), m.dose, m.route, m.freq].filter(Boolean).join(' ');
      items.push({ label: 'New med', text: parts, color: '#7DC4FF' });
    });
    // DC meds
    (p.medsList || []).filter(m => m.dc && m.dcDate && new Date(m.dcDate) >= ago24 && m.name && m.name.trim()).forEach(m =>
      items.push({ label: 'Med stopped', text: m.name.trim(), color: '#FF9500' })
    );
    // New IV fluids
    (p.ivList || []).filter(iv => !iv.dc && iv.addedAt && iv.addedAt >= cutoff && iv.fluid && iv.fluid.trim()).forEach(iv => {
      const txt = iv.fluid.trim() + (iv.rate ? ' @ ' + iv.rate : '');
      items.push({ label: 'New IVF', text: txt, color: '#64D2FF' });
    });
    // New diet
    (p.dietList || []).filter(d => !d.dc && d.addedAt && d.addedAt >= cutoff && d.order && d.order.trim()).forEach(d =>
      items.push({ label: 'Diet', text: d.order.trim(), color: '#A8D878' })
    );
    // New labs
    (p.labsLog || []).filter(g => g.date && new Date(g.date) >= ago24).forEach(g => {
      const vals = (g.entries || []).filter(e => e.name && e.value).map(e =>
        e.name + ' ' + e.value + (e.unit || '') + (e.flag ? ' (' + e.flag + ')' : '')
      ).join(', ');
      if (vals) items.push({ label: 'Labs', text: vals, color: '#BF5AF2' });
    });
    // New studies
    (p.studiesLog || []).filter(i => i.date && new Date(i.date) >= ago24).forEach(i => {
      const txt = [i.name, i.study, i.findings ? ': ' + i.findings : ''].filter(Boolean).join(' ').trim();
      if (txt) items.push({ label: 'Studies', text: txt, color: '#64D2FF' });
    });
    // Notes added in last 24h
    (p.soapNotes || []).filter(n => n.date && new Date(n.date) >= ago24).forEach(n => {
      const preview = n.a || n.p || n.s || '';
      if (preview) items.push({ label: 'Note' + (n.author ? ' · ' + n.author : ''), text: preview.slice(0, 80) + (preview.length > 80 ? '…' : ''), color: '#7DC4FF' });
    });
    // New vitals — only show fields that have values
    (p.vitalsLog || []).filter(v => v.date && new Date(v.date) >= ago24).slice(-1).forEach(v => {
      const parts = [];
      if (v.bp)   parts.push('BP ' + v.bp);
      if (v.hr)   parts.push('HR ' + v.hr);
      if (v.spo2) parts.push('SpO2 ' + v.spo2);
      if (v.temp) parts.push('T ' + v.temp);
      if (parts.length) items.push({ label: 'Vitals', text: parts.join(', '), color: '#FFD60A' });
    });
    // Tasks added in last 24h — show done/pending status
    (p.todos || []).filter(t => t.id && (t.addedAt >= cutoff || parseInt(t.id.replace('t',''), 10) >= cutoff)).forEach(t => {
      if (!t.text || !t.text.trim()) return;
      const status = t.done || t.status === 'Done' ? 'Done' : 'Pending';
      items.push({ label: 'Task · ' + status, text: t.text.trim(), color: status === 'Done' ? '#30D158' : '#FF9500' });
    });
    return items;
  };

  const copySum = () => { shareOrCopy(makeSummary()); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const makeFullBackup = (overridePts) => {
    const now = new Date();
    const pool = overridePts || pts;
    const header = `FULL PATIENT BACKUP — ${shiftCode}\n${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}\nTotal patients: ${pool.length}\n${'='.repeat(60)}\n\n`;
    const body = [...pool].sort((a, b) => UORD[a.urgency] - UORD[b.urgency]).map(p => {
      const tm = getTeam(p.team);
      const lines = [];
      lines.push(`[${URG[p.urgency].label.toUpperCase()}] ${p.name}`);
      lines.push(`MRN: ${p.mrn || '-'} | Room: ${p.room || '-'} | ${p.age || '?'}yo ${p.sex || ''}${p.weight ? ` | ${p.weight}${p.weightUnit || 'kg'}` : ''}`);
      if (p.dob) lines.push(`DOB: ${p.dob}`);
      lines.push(`Service: ${p.code || '-'}${tm ? ` | Team: ${tm.name}` : ''}${p.consultant ? ` | Consultant: ${p.consultant}` : ''}`);
      if (p.admissionDate) lines.push(`Admitted: ${p.admissionDate} (D${Math.max(1, Math.floor((Date.now() - new Date(p.admissionDate)) / 86400000) + 1)})`);
      const ro = RESUS_OPTIONS?.find(r => r.k === (p.resus || 'full'));
      if (ro) lines.push(`Resus: ${ro.label || ro.short || p.resus}`);
      lines.push(`Dx: ${p.diagnosis || '-'}`);
      if (p.allergies) lines.push(`Allergies: ${p.allergies}`);

      // History fields
      if (p.hpi || p.pmh || p.psh || p.fh || p.ros || p.pe || p.cc) {
        lines.push(`\nCLINICAL HISTORY:`);
        if (p.cc)  lines.push(`  CC: ${p.cc}`);
        if (p.hpi) lines.push(`  HPI: ${p.hpi}`);
        if (p.pmh) lines.push(`  PMH: ${p.pmh}`);
        if (p.psh) lines.push(`  PSH: ${p.psh}`);
        if (p.fh)  lines.push(`  FH: ${p.fh}`);
        if (p.ros) lines.push(`  ROS: ${p.ros}`);
        if (p.pe)  lines.push(`  PE: ${p.pe}`);
      }

      // Active issues
      const activeIss = (p.issuesList || []).filter(i => !i.resolved);
      if (activeIss.length) {
        lines.push(`\nACTIVE ISSUES (${activeIss.length}):`);
        activeIss.forEach(i => {
          lines.push(`  • ${i.text || '-'}`);
          if (i.note) lines.push(`    ${i.note}`);
        });
      }
      // Legacy activeIssues text blob
      if (p.activeIssues && !activeIss.length) lines.push(`\nIssues:\n${p.activeIssues}`);

      // Resolved issues
      const resolved = (p.resolvedIssues || []);
      if (resolved.length) {
        lines.push(`\nRESOLVED ISSUES:`);
        resolved.forEach(i => lines.push(`  ✓ ${i.text}${i.resolvedDate ? ` (resolved ${i.resolvedDate})` : ''}`));
      }

      // Tasks
      const todos = (p.todos || []);
      const pending = todos.filter(t => !t.done && t.status !== 'Done');
      const done = todos.filter(t => t.done || t.status === 'Done');
      if (todos.length) {
        lines.push(`\nTASKS:`);
        pending.forEach(t => lines.push(`  ☐ [${t.priority || 'routine'}] ${t.text}`));
        done.forEach(t => lines.push(`  ☑ ${t.text}`));
      }

      // Meds
      const meds = (p.medsList || []);
      const activeMeds = meds.filter(m => !m.dc);
      const dcMeds = meds.filter(m => m.dc);
      if (activeMeds.length) {
        lines.push(`\nMEDICATIONS (active):`);
        activeMeds.forEach(m => {
          let s = `  • ${m.name}${m.dose ? ' ' + m.dose : ''}${m.route ? ' ' + m.route : ''}${m.freq ? ' ' + m.freq : ''}`;
          if (m.startDate) s += ` — started ${m.startDate}`;
          if (m.indication) s += ` (${m.indication})`;
          lines.push(s);
        });
      }
      if (dcMeds.length) {
        lines.push(`MEDICATIONS (stopped):`);
        dcMeds.forEach(m => lines.push(`  ✕ ${m.name}${m.dose ? ' ' + m.dose : ''}${m.dcDate ? ` — stopped ${m.dcDate}` : ''}${m.dcReason ? ` (${m.dcReason})` : ''}`));
      }

      // IV fluids
      const ivs = (p.ivList || []).filter(iv => !iv.dc);
      if (ivs.length) {
        lines.push(`\nIV FLUIDS:`);
        ivs.forEach(iv => lines.push(`  • ${iv.fluid}${iv.rate ? ' @ ' + iv.rate : ''}${iv.duration ? ' x ' + iv.duration : ''}`));
      }

      // Diet
      const diet = (p.dietList || []).filter(d => !d.dc);
      if (diet.length) {
        lines.push(`\nDIET:`);
        diet.forEach(d => lines.push(`  • ${d.order}${d.note ? ' (' + d.note + ')' : ''}`));
      }

      // Vitals — using correct stored field names: vBP, vHR, vRR, vT, vO2, vAVPU, vGCSe/v/m
      const vitals = [...(p.vitalsLog || [])].sort((a, b) => (b.date + (b.time||'')).localeCompare(a.date + (a.time||'')));
      if (vitals.length) {
        lines.push(`\nVITALS (${vitals.length} entries, most recent first):`);
        vitals.forEach(v => {
          const gcsTotal = (v.vGCSe && v.vGCSv && v.vGCSm) ? parseInt(v.vGCSe)+parseInt(v.vGCSv)+parseInt(v.vGCSm) : null;
          const parts = [
            v.date, v.time,
            v.vBP   && `BP ${v.vBP}`,
            v.vHR   && `HR ${v.vHR}`,
            v.vRR   && `RR ${v.vRR}`,
            v.vT    && `T ${v.vT}°C`,
            v.vO2   && `SpO2 ${v.vO2}%`,
            v.vAVPU && v.vAVPU !== 'A' && `AVPU ${v.vAVPU}`,
            gcsTotal && `GCS ${gcsTotal} (E${v.vGCSe}V${v.vGCSv}M${v.vGCSm})`,
          ].filter(Boolean);
          lines.push(`  ${parts.join(' | ')}`);
        });
      }

      // Labs
      const labs = [...(p.labsLog || [])].sort((a, b) => b.date.localeCompare(a.date));
      if (labs.length) {
        lines.push(`\nLABS:`);
        labs.forEach(g => {
          lines.push(`  ${g.date}${g.time ? ' ' + g.time : ''}${g.label ? ' — ' + g.label : ''}:`);
          (g.entries || []).forEach(e => {
            if (!e.name && !e.value) return;
            lines.push(`    ${e.name}: ${e.value}${e.unit || ''}${e.flag ? ' ⚠ ' + e.flag : ''}`);
          });
        });
      }

      // Studies
      const imaging = (p.studiesLog || []);
      if (imaging.length) {
        lines.push(`\nSTUDIES:`);
        imaging.forEach(i => {
          lines.push(`  • ${i.date || ''} [${i.section || ''}] ${i.name || ''}${i.study ? ' — ' + i.study : ''}`.trim());
          if (i.result)   lines.push(`    Result: ${i.result}${i.organism ? ' (' + i.organism + ')' : ''}`);
          if (i.findings) lines.push(`    Findings: ${i.findings}`);
        });
      }

      // Procedures
      const procs = (p.procedureList || []);
      if (procs.length) {
        lines.push(`\nPROCEDURES:`);
        procs.forEach(pr => lines.push(`  • ${pr.name}${pr.date ? ' [' + pr.date + ']' : ''} | ${pr.status}${pr.physician ? ' | ' + pr.physician : ''}${pr.note ? ' — ' + pr.note : ''}`));
      }

      // I&O — each day has inputs[] and outputs[] arrays with {label, amount}
      const io = [...(p.ioLog || [])].sort((a, b) => (b.date||'').localeCompare(a.date||''));
      if (io.length) {
        lines.push(`\nI&O (FLUID BALANCE):`);
        io.forEach(day => {
          const totalIn  = (day.inputs  || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
          const totalOut = (day.outputs || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
          const balance  = totalIn - totalOut;
          lines.push(`  ${day.date} — In: ${totalIn}mL | Out: ${totalOut}mL | Balance: ${balance >= 0 ? '+' : ''}${balance}mL`);
          (day.inputs  || []).forEach(e => lines.push(`    IN:  ${e.label || '—'} ${e.amount || 0}mL`));
          (day.outputs || []).forEach(e => lines.push(`    OUT: ${e.label || '—'} ${e.amount || 0}mL`));
        });
      }

      // Discharge orders
      const dc = p.dischargeOrders;
      if (dc && (dc.medications || dc.followUp || dc.others)) {
        lines.push(`\nDISCHARGE ORDERS:`);
        if (dc.medications) lines.push(`  Medications: ${dc.medications}`);
        if (dc.followUp) lines.push(`  Follow-up: ${dc.followUp}`);
        if (dc.others) lines.push(`  Others: ${dc.others}`);
      }

      if (p.note) lines.push(`\nNote: ${p.note}`);

      // Progress notes (SOAP)
      const soapNotes = [...(p.soapNotes || [])].sort((a, b) => (b.date + (b.time||'')).localeCompare(a.date + (a.time||'')));
      if (soapNotes.length) {
        lines.push(`\nPROGRESS NOTES (${soapNotes.length}):`);
        soapNotes.forEach(n => {
          lines.push(`  [${n.date}${n.time ? ' ' + n.time : ''}${n.author ? ' — ' + n.author : ''}]`);
          if (n.s) lines.push(`    S: ${n.s}`);
          if (n.o) lines.push(`    O: ${n.o}`);
          if (n.a) lines.push(`    A: ${n.a}`);
          if (n.p) lines.push(`    P: ${n.p}`);
        });
      }

      if (p.lastReviewed) lines.push(`Last reviewed: ${new Date(p.lastReviewed).toLocaleString()}`);

      return lines.join('\n');
    }).join('\n\n' + '='.repeat(60) + '\n\n');

    return header + body + `\n\n${'='.repeat(60)}\nEnd of backup — ${pool.length} patients\n`;
  };

  const shareOrCopy = (text, fallbackMsg = 'Copied!') => {
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  const copyBackup = () => { shareOrCopy(makeFullBackup()); setBackedUp(true); setTimeout(() => setBackedUp(false), 2000); };
  const tSt = key => ({ flex: 1, padding: '10px 4px', background: 'none', border: 'none', borderBottom: tab === key ? '2px solid #4A9EFF' : '2px solid transparent', color: tab === key ? '#4A9EFF' : C.dim, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center', minWidth: 0 });


  // Shared MoreMenu props — avoids 5x repetition
  const mmp = {
    shiftCode, notifPerm,
    onRequestNotif: requestNotifPermission,
    onDisableNotif: disableNotifications,
    onAudit: () => setShowAudit(true),
    onFaq:   () => setShowFaq(true),
    onWardRound: () => setShowWardRound(true),
    onArchive: () => setShowArchive(true),
    onImport: () => setShowImport(true),
    onLeave: () => { if (!window.confirm('Leave this database? Code: ' + shiftCode)) return; localStorage.removeItem('hpwa_code'); if (shiftCode) unregisterDeviceFromShift(shiftCode); setShiftCode(null); },
    onPrint: () => copySum(),
    onBackup: copyBackup,
    teams, notifTeam, setNotifTeamPref, notifSs, setNotifSsPref, notifSvc, setNotifSvcPref, subspecs,
    copyTeam, setCopyTeamPref, copySs, setCopySsPref, copySvc, setCopySvcPref,
    roundTeam, setRoundTeamPref, roundSs, setRoundSsPref, roundSvc, setRoundSvcPref,
  };

  // ── Overlays — rendered via portal directly into document.body.
  // Portals sit outside the component tree entirely, so the main view
  // stays fully mounted during transitions — eliminates iOS repaint flicker.
  const overlaysJSX = createPortal(
    <>
      {showWardRound && <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}><WardRoundView pts={filtered} teams={teams} subspecs={subspecs} tf={tf} initialTeam={roundTeam} initialSs={roundSs} initialSvc={roundSvc} get24hChanges={get24hChanges} startIdx={wardRoundIdx} onIdxChange={setWardRoundIdx} onClose={() => setShowWardRound(false)} onOpenChart={(id, idx) => { setSelId(id); setTab('notes'); setView('detail'); setShowWardRound(false); setWardRoundIdx(idx); }} onMarkReviewed={markReviewed} patchPt={patchById} shiftCode={shiftCode} /></div>}
      {showAllTasks && <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}><AllTasksView pts={pts} teams={teams} subspecs={subspecs} teamFilter={atTeamFilter} setTeamFilter={setAtTeamFilter} ssFilter={atSsFilter} setSsFilter={setAtSsFilter} svcFilter={atSvcFilter} setSvcFilter={setAtSvcFilter} showDone={atShowDone} setShowDone={setAtShowDone} onClose={() => setShowAllTasks(false)} onSelectPt={id => { setSelId(id); setTab('tasks'); setView('detail'); setShowAllTasks(false); }} onToggleTask={(ptId, taskId, newStatus) => toggleTaskForPt(ptId, taskId, newStatus)} shiftCode={shiftCode} /></div>}
      {showArchive   && <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}><ArchiveView onBack={() => setShowArchive(false)} shiftCode={shiftCode} onBackupPt={p => shareOrCopy(makeFullBackup([p]))} /></div>}
      {showAudit     && <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}><AuditView shiftCode={shiftCode} pts={pts} teams={teams} subspecs={subspecs} onClose={() => setShowAudit(false)} /></div>}
      {showFaq       && <FAQView onClose={() => setShowFaq(false)} />}
      {showImport    && <ImportModal currentCode={shiftCode} currentPts={pts} currentTeams={teams} currentSubspecs={subspecs} onClose={() => setShowImport(false)} onMergeHere={(srcPts, srcTeams, srcSubspecs) => {
        const newPts = srcPts.map(p => ({ ...p, id: `${p.id}_imp_${Date.now()}_${Math.random().toString(36).slice(2,5)}` }));
        const mergedTeams = [...teams, ...srcTeams.filter(t => !teams.find(e => e.id === t.id))];
        const mergedSubs  = [...subspecs, ...srcSubspecs.filter(s => !subspecs.find(e => e.id === s.id))];
        const allPts = [...pts, ...newPts];
        save(allPts);
        setTeams(mergedTeams);
        setSubspecs(mergedSubs);
        stor.saveAll(allPts, mergedTeams, mergedSubs);
        setShowImport(false);
      }} />}
    </>,
    document.body
  );

  // ── TEAM MODAL — plain JSX (not a component) to prevent input cursor-jump on keystroke ──
  const teamModalJSX = showTM ? (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: C.bg1, borderRadius: '18px 18px 0 0', padding: '20px 16px 32px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#E8F4FF', flex: 1 }}>Manage Teams</span>
          <button onClick={() => setShowTM(false)} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {teams.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.bg2, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: t.color + '22', border: `2px solid ${t.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: t.color }}>{t.icon}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: t.color }}>{t.name}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{pts.filter(p => p.team === t.id).length} pts</span>
            <button onClick={() => delTeam(t.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        ))}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <input value={newTN} onChange={e => setNewTN(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTeam()} placeholder="New team name…"
            style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
          <button onClick={addTeam} style={{ background: '#1A3D6A', color: '#7DC4FF', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>Add</button>
        </div>
      </div>
    </div>
  ) : null;

  const subspecModalJSX = showSM ? (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: C.bg1, borderRadius: '18px 18px 0 0', padding: '20px 16px 32px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#E8F4FF', flex: 1 }}>Manage Subspecialties</span>
          <button onClick={() => setShowSM(false)} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {subspecs.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.bg2, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: s.color }}>{s.name}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{pts.filter(p => p.subspec === s.id).length} pts</span>
            <button onClick={() => delSubspec(s.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        ))}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <input value={newSSN} onChange={e => setNewSSN(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSubspec()} placeholder="New subspecialty…"
            style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
          <button onClick={addSubspec} style={{ background: '#1A3D6A', color: '#7DC4FF', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>Add</button>
        </div>
      </div>
    </div>
  ) : null;

  // ════════════════════════════════════════════
  // DESKTOP MODE — sidebar + main content panel
  // ════════════════════════════════════════════
  if (viewMode === 'desktop') {
    // Shared sidebar (patient list)
    const tSt2 = k => ({ padding: '10px 14px', fontSize: 13, fontWeight: tab === k ? 700 : 400, color: tab === k ? '#E0EEFA' : C.dim, background: tab === k ? '#1A2A3A' : 'transparent', border: 'none', cursor: 'pointer', borderBottom: tab === k ? '2px solid #4A9EFF' : '2px solid transparent', whiteSpace: 'nowrap', flexShrink: 1 });

    // Restore saved scroll position after sidebar renders
    const restoreSidebarScroll = (el) => {
      if (el && sidebarScrollRef._savedTop != null) {
        el.scrollTop = sidebarScrollRef._savedTop;
      }
    };

    const sidebarJSX = (
      <DesktopSidebar width="100%" flex>
        {/* Title + Icons — single row */}
        <div className="topbar" style={{ borderBottom: `1px solid ${C.bd}`, background: C.bg0, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#E8F4FF', flexShrink: 0 }}>🏥 Patient Tracker</span>
            <div style={{ flex: 1 }} />
            <ModeToggle viewMode={viewMode} setVM={setVM} />
            <button onClick={() => setShowAllTasks(true)} title="All tasks"
              style={{ background: '#1A0A2A', color: '#BF5AF2', border: '1px solid #3A1A5A', borderRadius: 8, padding: '4px 7px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
              ✓ {pts.filter(p => p.urgency !== 'discharged' && p.urgency !== 'mortality' && (atTeamFilter === 'all' || p.team === atTeamFilter) && (atSsFilter === 'all' || p.subspec === atSsFilter) && (atSvcFilter === 'all' || p.code === atSvcFilter)).reduce((n, p) => n + (p.todos || []).filter(t => (t.status || 'Pending') !== 'Done' && !t.done).length, 0) || ''}
            </button>
            <button onClick={() => setView('summary')} style={{ background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Summary</button>
            <MoreMenu {...mmp} direction="down" btnStyle={{ padding: '4px 8px', borderRadius: 8, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 15, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }} />
          </div>
        </div>

        <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
        <Ledger pts={pts} tf={tf} />

        {/* Team + Service filters — collapsible */}
        <div style={{ borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
          <div onClick={() => setFiltersOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Filters</span>
            <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {tf !== 'all' && (() => { const t = getTeam(tf); return t ? <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.color + '22', border: `1px solid ${t.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{t.name}</span> : null; })()}
              {ssFilter !== 'all' && (() => { const s = subspecs.find(x => x.id === ssFilter); return s ? <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.color + '22', border: `1px solid ${s.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{s.name}</span> : null; })()}
              {svcFilter !== 'all' && <span style={{ fontSize: 10, fontWeight: 700, color: CCOL[svcFilter] || '#AAA', background: (CCOL[svcFilter] || '#AAA') + '22', border: `1px solid ${(CCOL[svcFilter] || '#AAA')}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{svcFilter}</span>}
              {tf === 'all' && svcFilter === 'all' && ssFilter === 'all' && <span style={{ fontSize: 10, color: C.dim2 }}>All teams · All subspecs · All services</span>}
            </div>
            <span style={{ fontSize: 10, color: C.dim2, flexShrink: 0, display: 'inline-block', transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
          </div>
          {filtersOpen && (<>
            <div style={{ display: 'flex', gap: 5, overflowX: 'auto', padding: '0 14px 5px', alignItems: 'center' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Team</div>
              {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => {
                const cnt = t.id === 'all' ? 0 : pts.filter(p => p.team === t.id).length;
                return (
                  <button key={t.id} onClick={() => setTf(t.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${tf === t.id ? t.color : C.bd}`, background: tf === t.id ? t.color + '22' : C.bg2, color: tf === t.id ? t.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <span>{t.name}</span>
                    {cnt > 0 && <span style={{ background: tf === t.id ? t.color + '33' : C.bd, borderRadius: 10, padding: '0 5px', fontSize: 10, color: tf === t.id ? t.color : C.dim }}>{cnt}</span>}
                  </button>
                );
              })}
              <button onClick={() => setShowTM(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>⚙ Teams</button>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '0 14px 5px', overflowX: 'auto' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Subspec</div>
              {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...subspecs.map(s => ({ ...s, label: s.name }))].map(opt => {
                const cnt = opt.id === 'all' ? 0 : pts.filter(p => (tf === 'all' || p.team === tf) && p.subspec === opt.id).length;
                const active = ssFilter === opt.id;
                return (
                  <button key={opt.id} onClick={() => setSsFilter(opt.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {opt.label}
                    {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                  </button>
                );
              })}
              <button onClick={() => setShowSM(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>⚙ Edit</button>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '0 14px 8px', overflowX: 'auto' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Service</div>
              {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => {
                const cnt = opt.id === 'all' ? 0 : pts.filter(p => (tf === 'all' || p.team === tf) && p.code === opt.id).length;
                const active = svcFilter === opt.id;
                return (
                  <button key={opt.id} onClick={() => setSvcFilter(opt.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {opt.label}
                    {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                  </button>
                );
              })}
            </div>
          </>)}
        </div>

        {/* Urgency tally — mirrors mobile */}
        <div className="tally-grid" style={{ display: 'flex', gap: 5, padding: '6px 14px', flexShrink: 0 }}>
          {Object.entries(URG).map(([k, u]) => {
            const n = preDisp.filter(p => p.urgency === k).length;
            const active = dispFilter === k;
            return (
              <button key={k} onClick={() => setDispFilter(active ? 'all' : k)}
                style={{ flex: 1, background: active ? u.bg : C.bg2, border: `1px solid ${active ? u.color : u.color + '33'}`, borderRadius: 8, padding: '6px 4px', textAlign: 'center', cursor: 'pointer', minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: u.color }}>{n}</div>
                <div className="tally-label" style={{ fontSize: 8, color: u.color + '99', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
              </button>
            );
          })}
        </div>
        {/* Search + Add — between tally and patient list */}
        <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.bd}`, background: C.bg1, flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '7px 10px', color: C.tx, fontSize: 12, outline: 'none', minWidth: 0 }} />
          <button onClick={() => setDense(!denseMode)} title={denseMode ? 'Card view' : 'Dense view'}
            style={{ background: denseMode ? '#1A3D6A' : C.bg2, color: denseMode ? '#7DC4FF' : C.dim, border: '1px solid ' + (denseMode ? '#4A9EFF' : C.bd), borderRadius: 8, padding: '0 9px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, height: 34, display: 'flex', alignItems: 'center' }}>
            {denseMode ? '☰' : '▤'}
          </button>
          <button onClick={() => { setNewPt(blankPt(tf !== 'all' ? tf : '')); setView('add'); }}
            style={{ width: 34, height: 34, borderRadius: '50%', background: '#1A3D6A', color: '#7DC4FF', border: '2px solid #4A9EFF', fontSize: 22, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 10px rgba(74,158,255,0.4)', lineHeight: 1 }}>+</button>
        </div>
        {/* Patient list */}
        <DesktopSidebarContent
          scrollRef={el => { sidebarScrollRef.current = el; restoreSidebarScroll(el); }}
          onScroll={e => { sidebarScrollRef._savedTop = e.currentTarget.scrollTop; }}
        >
          {sync === 'loading' && <div style={{ textAlign: 'center', color: C.dim, padding: '30px 0', fontSize: 12 }}>Loading…</div>}
          {sync !== 'loading' && filtered.length === 0 && <div style={{ textAlign: 'center', padding: '30px 0', color: C.dim, fontSize: 12 }}>No patients found</div>}
          <div style={{ padding: '8px 10px' }}>
            {filtered.map(p => {
              const u = URG[p.urgency], tm = getTeam(p.team), pend = (p.todos || []).filter(t => !t.done).length;
              const urgentTasks = (p.todos || []).filter(t => !t.done && (t.priority || 'routine') === 'urgent').length;
              const isSelected = selId === p.id;

              const lastSaved = p.lastModified || 0;
              const isNew = lastSaved > lastLoginAt && lastLoginAt > 0;
              const lastRev = p.lastReviewed;
              const staleThresh = p.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT;
              const admittedAt = p.admissionDate ? new Date(p.admissionDate).getTime() : 0;
              const isStale = lastRev ? (Date.now() - lastRev > staleThresh) : (admittedAt > 0 && Date.now() - admittedAt > staleThresh);
              const revMins = lastRev ? Math.floor((Date.now() - lastRev) / 60000) : null;

              if (denseMode) {
                return (
                  <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: isSelected ? '#0A1E2E' : isNew ? '#0A1E12' : C.bg3, border: `1px solid ${isSelected ? '#4A9EFF' : isStale ? '#FF450044' : isNew ? '#30D15844' : C.bd}`, borderLeft: `3px solid ${u.color}`, borderRadius: 7, padding: '7px 10px', marginBottom: 3, cursor: 'pointer' }}>
                    <span style={{ fontSize: 10, color: C.dim, width: 28, flexShrink: 0, fontWeight: 700 }}>{p.room || '?'}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || 'Unnamed'}</span>
                    <span style={{ fontSize: 11, color: '#6A9BBC', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.diagnosis || ''}</span>
                    {urgentTasks > 0 && <span style={{ fontSize: 9, fontWeight: 900, color: '#FF4545', background: 'rgba(255,69,69,0.15)', border: '1px solid #FF454544', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{urgentTasks}!</span>}
                    {pend > 0 && !urgentTasks && <span style={{ fontSize: 10, color: '#FF9500', flexShrink: 0 }}>⏳{pend}</span>}
                    {isStale && <span style={{ fontSize: 9, color: '#FF4545', flexShrink: 0 }}>⚠{lastRev ? (revMins >= 60 ? Math.floor(revMins / 60) + 'h' : revMins + 'm') : '!'}</span>}
                    {isNew && !isStale && <span style={{ fontSize: 8, fontWeight: 900, color: '#30D158', background: '#30D15822', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>UPDATED</span>}
                    <button onClick={e => { e.stopPropagation(); markReviewed(p.id); }}
                      style={{ background: !isStale ? '#081A0C' : 'none', border: '1px solid ' + (!isStale ? '#1A3A1E' : C.bd), borderRadius: 6, color: !isStale ? '#30D158' : C.dim2, fontSize: 9, padding: '2px 6px', cursor: 'pointer', flexShrink: 0 }}>✓</button>
                  </div>
                );
              }
              return (
                <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
                  style={{ background: isSelected ? '#0A1E2E' : C.bg3, border: `1px solid ${isSelected ? '#4A9EFF' : C.bd}`, borderLeft: `3px solid ${u.color}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6, cursor: 'pointer' }}>
                  {/* Row 1: name + UBadge + NEW/resus */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#E0EEFA', flex: 1 }}>{p.name || 'Unnamed'}</span>

                    {(() => { const ro = RESUS_OPTIONS.find(r => r.k === (p.resus || 'full')); return ro && ro.k !== 'full' ? <span style={{ fontSize: 8, fontWeight: 900, color: ro.color, background: ro.bg, border: '1px solid ' + ro.color + '66', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{ro.short}</span> : null; })()}
                    <UBadge u={p.urgency} />
                  </div>
                  {/* Row 2: Rm · age · weight · D · consultant */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontSize: 10, color: C.dim }}>Rm {p.room}</span>
                    <span style={{ fontSize: 10, color: C.dim }}>{p.age}{p.sex}</span>
                    {p.weight && <span style={{ fontSize: 10, color: C.dim }}>{p.weight} {p.weightUnit || 'kg'}</span>}
                    {p.admissionDate && hospDay(p.admissionDate) !== null && <span style={{ fontSize: 10, color: '#A8C8A0', fontWeight: 700 }}>D{hospDay(p.admissionDate)}</span>}
                    {p.consultant && <span style={{ fontSize: 10, color: '#7DC4FF' }}>👤 {p.consultant}</span>}
                  </div>
                  {/* Row 3: Team · Subspec badges */}
                  {((tm && tf === 'all') || subspecs.find(s => s.id === p.subspec)) && (
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 3 }}>
                      {tm && tf === 'all' && <span style={{ fontSize: 9, fontWeight: 700, color: tm.color, background: tm.color + '22', border: '1px solid ' + tm.color + '44', padding: '2px 6px', borderRadius: 4 }}>{tm.name}</span>}
                      {(() => { const ss = subspecs.find(s => s.id === p.subspec); return ss ? <span style={{ fontSize: 9, fontWeight: 700, color: ss.color || '#7DC4FF', background: (ss.color || '#7DC4FF') + '22', border: '1px solid ' + (ss.color || '#7DC4FF') + '44', padding: '2px 6px', borderRadius: 4 }}>{ss.name}</span> : null; })()}
                    </div>
                  )}
                  {/* Row 4: diagnosis */}
                  <div style={{ fontSize: 12, color: '#6A9BBC', marginBottom: (pend || urgentTasks || isStale || lastRev) ? 5 : 0 }}>{p.diagnosis || 'No diagnosis'}</div>
                  {/* Row 5: task badges + time ago + Reviewed flush right */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {urgentTasks > 0 && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,69,69,0.12)', border: '1px solid #FF454544', borderRadius: 6, padding: '2px 8px', fontSize: 10, color: '#FF4545', fontWeight: 700 }}>🚨 {urgentTasks} urgent</div>}
                    {pend > 0 && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FF950015', border: '1px solid #FF950044', borderRadius: 6, padding: '2px 8px', fontSize: 10, color: '#FF9500', fontWeight: 600 }}>⏳ {pend} pending</div>}
                    {isStale
                      ? <span style={{ fontSize: 10, color: '#FF4545' }}>⚠ {lastRev ? (revMins >= 60 ? Math.floor(revMins / 60) + 'h' : revMins + 'm') + ' since review' : 'Not reviewed'}</span>
                      : lastRev && <span style={{ fontSize: 10, color: '#30D15888' }}>✓ {revMins < 60 ? revMins + 'm' : Math.floor(revMins / 60) + 'h'} ago</span>}
                    <button onClick={e => { e.stopPropagation(); markReviewed(p.id); }}
                      style={{ marginLeft: 'auto', background: !isStale ? '#081A0C' : 'none', border: '1px solid ' + (!isStale ? '#1A3A1E' : C.bd), borderRadius: 6, color: !isStale ? '#30D158' : C.dim2, fontSize: 10, padding: '2px 8px', cursor: 'pointer' }}>✓ Reviewed</button>
                  </div>
                </div>
              );
            })}
          </div>
        </DesktopSidebarContent>
      </DesktopSidebar>
    );

    // ── DESKTOP: No patient selected ──
    if (view === 'list' || (!cur && view !== 'add' && view !== 'summary')) return (
      <DesktopShell>
        {teamModalJSX}
      {subspecModalJSX}
        {subspecModalJSX}
        {showArchive && <ArchiveView shiftCode={shiftCode} onClose={() => setShowArchive(false)} onBackupPt={p => shareOrCopy(makeFullBackup([p]))} />}
<div style={{ width: '35vw', minWidth: '300px', maxWidth: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: C.bg1, borderRight: `1px solid ${C.bd}` }}>
          {sidebarJSX}
        </div>
        <DesktopMain>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: C.dim }}>
            <div style={{ fontSize: 48 }}>👈</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Select a patient to view details</div>
            <div style={{ fontSize: 12, color: C.dim2 }}>or use + Add Patient to create a new one</div>
          </div>
        </DesktopMain>
      {overlaysJSX}
      </DesktopShell>
    );

    // ── DESKTOP: Detail view ──
    if ((view === 'detail' || view === 'edit') && cur) {
      const u = URG[cur.urgency], tm = getTeam(cur.team);
      const todos = [...(cur.todos || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const pend = todos.filter(t => !t.done).length;
      const flagged = (cur.labsLog || []).flatMap(g => g.entries.filter(e => e.flag));
      const tabs = [
        { k: 'issues',  lb: 'Issues' },
        { k: 'notes',   lb: 'Notes'   },
        { k: 'vitals',  lb: 'Vitals' },
        { k: 'meds',    lb: 'Meds' },
        { k: 'labs',    lb: 'Labs' },
        { k: 'imaging', lb: 'Studies' },
        { k: 'refer',   lb: 'Refer' },
        { k: 'tasks',   lb: `Tasks${pend ? ` (${pend})` : ''}` },
        ...(['mgh','discharged','mortality'].includes(cur.urgency) ? [{ k: 'discharge', lb: 'Discharge' }] : []),
      ];
      return (
        <DesktopShell>
          {teamModalJSX}
      {subspecModalJSX}
        {subspecModalJSX}
          {showArchive && <ArchiveView shiftCode={shiftCode} onClose={() => setShowArchive(false)} onBackupPt={p => shareOrCopy(makeFullBackup([p]))} />}
  <div style={{ width: '35vw', minWidth: '300px', maxWidth: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: C.bg1, borderRight: `1px solid ${C.bd}` }}>
          {sidebarJSX}
        </div>
          <DesktopMain>
            {/* Patient header */}
            <div className="topbar desktop-topbar" style={{ background: u.bg, borderBottom: `2px solid ${u.color}44`, flexShrink: 0 }}>
              {/* Row 1: badges (Primary · Team · Subspec · NEWS2 · GCS) + action buttons far right */}
              {(() => {
                const bdg = { fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 5, lineHeight: 1, display: 'inline-flex', alignItems: 'center', flexShrink: 0 };
                const lv  = [...(cur.vitalsLog||[])].sort((a,b)=>(b.date+b.time).localeCompare(a.date+a.time))[0];
                const n2  = calcNEWS2(lv);
                const gcs = calcGCS(lv);
                const ss  = subspecs.find(s => s.id === cur.subspec);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, flexWrap: 'wrap' }}>
                    <CBadge code={cur.code} />
                    {tm  && <span style={{ ...bdg, color: tm.color,  background: tm.color  + '22', border: '1px solid ' + tm.color  + '44' }}>{tm.name}</span>}
                    {ss  && <span style={{ ...bdg, color: SSCOLS[ss.id] || '#7DC4FF', background: (SSCOLS[ss.id] || '#7DC4FF') + '22', border: '1px solid ' + (SSCOLS[ss.id] || '#7DC4FF') + '44' }}>{ss.name}</span>}
                    {n2  && <span style={{ ...bdg, color: n2.color,  background: n2.bg,  border: '1px solid ' + n2.color  + '44', fontWeight: 900 }} title={n2.reasons.join(', ')}>NEWS2 {n2.score}</span>}
                    {gcs && <span style={{ ...bdg, color: gcs.color, background: gcs.bg, border: '1px solid ' + gcs.color + '44', fontWeight: 900 }} title={`${gcs.risk} · E${gcs.e}V${gcs.v}M${gcs.m}`}>GCS {gcs.total}</span>}
                    <div style={{ flex: 1 }} />
                    <button onClick={() => setShowWardRound(true)} title="Start Rounds"
                      style={{ background: '#0A1A08', color: '#A8D878', border: '1px solid #2A4A14', borderRadius: 8, padding: '4px 9px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>🔄</button>
                    <button onClick={() => consultNotify(cur)} title="Send consultant update"
                      style={{ background: '#0A1A2A', color: '#7DC4FF', border: '1px solid #2A4A6A', borderRadius: 8, padding: '4px 9px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>📤</button>
                    <button onClick={() => { shareOrCopy(makeFullBackup([cur])); }} title="Copy full patient backup"
                      style={{ background: '#0A1A2A', color: '#64D2FF', border: '1px solid #1A3A4A', borderRadius: 8, padding: '4px 9px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>💾</button>
                    <button onClick={() => setView(view === 'edit' ? 'detail' : 'edit')}
                      style={{ background: view === 'edit' ? '#081A0C' : C.bg2, color: view === 'edit' ? '#30D158' : C.dim, border: `1px solid ${view === 'edit' ? '#1A3A1E' : C.bd}`, borderRadius: 8, padding: '4px 9px', fontSize: 11, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>
                      {view === 'edit' ? '✓ Done' : '✎ Edit'}
                    </button>
                    <button onClick={() => { if (!window.confirm('Remove this patient?')) return; if (!window.confirm('Are you sure?')) return; delPt(cur.id); setView('list'); }}
                      style={{ background: '#1A0808', color: '#FF6B6B', border: '1px solid #3A1515', borderRadius: 8, padding: '4px 9px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>🗑</button>
                  </div>
                );
              })()}
              {/* Row 2: patient name */}
              <div style={{ fontSize: 16, fontWeight: 800, color: '#E8F4FF', marginBottom: 3 }}>{cur.name}</div>
              {/* Row 3: meta info */}
              <div style={{ fontSize: 11, color: C.dim, marginBottom: 5 }}>Rm {cur.room} · {cur.age ? `${cur.age}yo ` : ''}{cur.sex}{cur.weight ? ` · ${cur.weight}${cur.weightUnit || 'kg'}` : ''}{cur.admissionDate && hospDay(cur.admissionDate) ? ` · D${hospDay(cur.admissionDate)}` : ''}{cur.consultant ? ` · 👤 ${cur.consultant}` : ''}</div>
              {/* Row 4: diagnosis */}
              <div style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA', marginBottom: 6 }}>{cur.diagnosis || 'No diagnosis'}</div>
              {/* Row 5: Status / Resus / Subspec — collapsible groups */}
              {(() => {
                const urgObj   = URG[cur.urgency] || URG.stable;
                const resusObj = RESUS_OPTIONS.find(r => r.k === (cur.resus || 'full')) || RESUS_OPTIONS[0];
                const ssObj    = subspecs.find(s => s.id === cur.subspec);
                const ssBtnCol = ssObj ? (SSCOLS[ssObj.id] || '#7DC4FF') : C.dim2;
                const pillBase = { padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' };
                const toggle   = (g) => setExpandGroup(prev => prev === g ? null : g);
                const selPill  = (label, color, bg, group) => (
                  <button onClick={() => toggle(group)}
                    style={{ ...pillBase, border: `1px solid ${color}`, background: expandGroup === group ? bg : 'transparent', color }}>
                    {label} {expandGroup === group ? '▲' : '▾'}
                  </button>
                );
                const reviewedFresh = cur.lastReviewed && (Date.now() - cur.lastReviewed) < (cur.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                      {selPill(urgObj.label,   urgObj.color,   urgObj.bg,   'status')}
                      {selPill(resusObj.label, resusObj.color, resusObj.bg, 'resus')}
                      {subspecs.length > 0 && selPill(ssObj ? ssObj.name : 'No Subspec', ssBtnCol, ssBtnCol + '22', 'subspec')}
                      <div style={{ flex: 1 }} />
                      <button onClick={() => markReviewed(selId)}
                        style={{ ...pillBase, background: reviewedFresh ? '#081A0C' : 'transparent', color: reviewedFresh ? '#30D158' : C.dim2, border: '1px solid ' + (reviewedFresh ? '#1A3A1E' : C.bd), fontWeight: reviewedFresh ? 700 : 400 }}>✓ Reviewed</button>
                    </div>
                    {expandGroup === 'status' && (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 4, borderLeft: `2px solid ${C.bd}` }}>
                        {Object.entries(URG).map(([k, urg]) => (
                          <button key={k} onClick={() => { setFld('urgency', k); setExpandGroup(null); }}
                            style={{ ...pillBase, border: '1px solid ' + (cur.urgency === k ? urg.color : C.bd), background: cur.urgency === k ? urg.bg : 'transparent', color: cur.urgency === k ? urg.color : C.dim2, fontWeight: cur.urgency === k ? 800 : 400 }}>
                            {urg.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {expandGroup === 'resus' && (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 4, borderLeft: `2px solid ${C.bd}` }}>
                        {RESUS_OPTIONS.map(ro => (
                          <button key={ro.k} onClick={() => { setFld('resus', ro.k); setExpandGroup(null); }}
                            style={{ ...pillBase, border: '1px solid ' + ((cur.resus||'full') === ro.k ? ro.color : C.bd), background: (cur.resus||'full') === ro.k ? ro.bg : 'transparent', color: (cur.resus||'full') === ro.k ? ro.color : C.dim2, fontWeight: (cur.resus||'full') === ro.k ? 800 : 400 }}>
                            {ro.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {expandGroup === 'subspec' && subspecs.length > 0 && (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 4, borderLeft: `2px solid ${C.bd}` }}>
                        <button onClick={() => { setFld('subspec', ''); setExpandGroup(null); }}
                          style={{ ...pillBase, border: '1px solid ' + (!cur.subspec ? '#4A9EFF' : C.bd), background: !cur.subspec ? '#1A3D6A' : 'transparent', color: !cur.subspec ? '#7DC4FF' : C.dim2, fontWeight: !cur.subspec ? 800 : 400 }}>
                          None
                        </button>
                        {subspecs.map(ss => {
                          const col = SSCOLS[ss.id] || '#7DC4FF';
                          return (
                            <button key={ss.id} onClick={() => { setFld('subspec', ss.id); setExpandGroup(null); }}
                              style={{ ...pillBase, border: '1px solid ' + (cur.subspec === ss.id ? col : C.bd), background: cur.subspec === ss.id ? col + '22' : 'transparent', color: cur.subspec === ss.id ? col : C.dim2, fontWeight: cur.subspec === ss.id ? 800 : 400 }}>
                              {ss.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Edit mode or Tab mode */}
            {view === 'edit' ? (
              <DesktopContent>
                {/* Inline edit form — reuse same cards but split 2-col */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <Card>
                      <SLabel>Patient Info</SLabel>
                      <IFld label="Full Name" value={cur.name} onChange={v => setFld('name', v)} />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                        <IFld label="MRN"  value={cur.mrn}  onChange={v => setFld('mrn',  v)} />
                        <IFld label="Room" value={cur.room} onChange={v => setFld('room', v)} />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Birth</div>
                        <div style={{ overflow: 'hidden', width: '100%' }}><input type="date" value={cur.dob || ''} onChange={e => { const d = e.target.value; setFld('dob', d); setFld('age', calcAge(d)); }}
                          style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: cur.dob ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} /></div>
                        {cur.dob && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>Age: {calcAge(cur.dob)} years old</div>}
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Weight</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input value={cur.weight || ''} onChange={e => setFld('weight', e.target.value)} placeholder="e.g. 70"
                            style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
                          <select value={cur.weightUnit || 'kg'} onChange={e => setFld('weightUnit', e.target.value)}
                            style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 8px', color: C.dim, fontSize: 12, flexShrink: 0 }}>
                            <option value="kg">kg</option><option value="lbs">lbs</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Sex</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {['M', 'F', 'Other'].map(s => (
                            <button key={s} onClick={() => setFld('sex', s)}
                              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.sex === s ? '#4A9EFF' : C.bd}`, background: cur.sex === s ? '#1A3D6A' : 'transparent', color: cur.sex === s ? '#7DC4FF' : C.dim, cursor: 'pointer', fontSize: 13 }}>
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                      <IFld label="Consultant" value={cur.consultant || ''} onChange={v => setFld('consultant', v)} />
                      <div style={{ marginBottom: 12, overflow: 'hidden' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Admission</div>
                        <input type="date" value={cur.admissionDate || ''} onChange={e => setFld('admissionDate', e.target.value)}
                          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: cur.admissionDate ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
                        {cur.admissionDate && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>D{hospDay(cur.admissionDate)} (admitted {new Date(cur.admissionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })})</div>}
                      </div>
                    </Card>
                  </div>
                  <div>
                    <Card>
                      <SLabel>Team</SLabel>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        {teams.map(t => (
                          <button key={t.id} onClick={() => setFld('team', t.id)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.team === t.id ? t.color : C.bd}`, background: cur.team === t.id ? t.color + '22' : 'transparent', color: cur.team === t.id ? t.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </Card>
                    {subspecs.length > 0 && (
                      <Card>
                        <SLabel>Subspec</SLabel>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => setFld('subspec', '')}
                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${!cur.subspec ? '#C8DFF0' : C.bd}`, background: !cur.subspec ? '#C8DFF022' : 'transparent', color: !cur.subspec ? '#C8DFF0' : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            None
                          </button>
                          {subspecs.map(s => (
                            <button key={s.id} onClick={() => setFld('subspec', s.id)}
                              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.subspec === s.id ? s.color : C.bd}`, background: cur.subspec === s.id ? s.color + '22' : 'transparent', color: cur.subspec === s.id ? s.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                              {s.name}
                            </button>
                          ))}
                        </div>
                      </Card>
                    )}
                    <Card>
                      <SLabel>Status</SLabel>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 6 }}>Disposition</div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                        {Object.entries(URG).map(([k, u]) => (
                          <button key={k} onClick={() => setFld('urgency', k)}
                            style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${cur.urgency === k ? u.color : C.bd}`, background: cur.urgency === k ? u.bg : 'transparent', color: cur.urgency === k ? u.color : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                            {u.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 6 }}>Service</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {CLIST.map(c => { const cc = CCOL[c] || '#AAA'; return (
                          <button key={c} onClick={() => setFld('code', c)}
                            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${cur.code === c ? cc : C.bd}`, background: cur.code === c ? cc + '22' : 'transparent', color: cur.code === c ? cc : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                            {c}
                          </button>
                        ); })}
                      </div>
                    </Card>
                    <Card>
                      <SLabel>Clinical</SLabel>
                      <IFld label="Primary Diagnosis" value={cur.diagnosis} onChange={v => setFld('diagnosis', v)} />
                      <IFld label="Allergies"         value={cur.allergies} onChange={v => setFld('allergies', v)} />
                    </Card>
                    <Card s={{ background: '#0D1A10', border: '1px solid #1A3A1E' }}>
                      <SLabel color="#30D158">Archive</SLabel>
                      <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Manually move this patient to the archive.</div>
                      <WBtn onClick={() => { if (!window.confirm(`Archive ${cur.name || 'this patient'}?`)) return; manualArchive(cur); }} bg="#081A0C" color="#30D158" bdr="1px solid #1A3A1E">📦 Archive This Patient</WBtn>
                    </Card>
                  </div>
                </div>
              </DesktopContent>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                {/* Tabs bar */}
                <div style={{ display: 'flex', borderBottom: `1px solid ${C.bd}`, background: C.bg0, flexShrink: 0, flexWrap: 'wrap' }}>
                  {tabs.map(t => <button key={t.k} onClick={() => setTab(t.k)} style={tSt2(t.k)}>{t.lb}</button>)}
                </div>
                <DesktopContent>
                  {tab === 'issues' && <IssuesTab cur={cur} setFld={setFld} patch={patch} showResolved={showResolved} setShowResolved={setShowResolved} onNotify={(title, body) => sendPushNotif(title, body, cur)} />}
                  {tab === 'vitals' && <VitalsTab cur={cur} vd={vd} setVd={setVd} saveVitals={saveVitals} delVitals={delVitals} getIODay={getIODay} addIOEntry={addIOEntry} editIOEntry={editIOEntry} delIOEntry={delIOEntry} delIODay={delIODay} />}
                  {tab === 'tasks'  && <TasksTab todos={todos} addTodo={addTodo} togTodo={togTodo} editTodo={editTodo} delTodo={delTodo} moveTodo={moveTodo} dropTodo={dropTodo} dragIdx={dragIdx} setDragIdx={setDragIdx} dragOver={dragOver} setDragOver={setDragOver} onAddLabResult={() => setTab('labs')} onAddImagingResult={() => setTab('imaging')} />}
                  {tab === 'labs'   && <LabsTab cur={cur} ldate={ldate} setLdate={setLdate} ltime={ltime} setLtime={setLtime} addLabSet={addLabSet} addLabRow={addLabRow} editLab={editLab} delLabRow={delLabRow} delLabSet={delLabSet} saveLabSet={saveLabSet} clearLabFlag={clearLabFlag} />}
                  {tab === 'meds'    && <MedsTab    cur={cur} patch={patch} setFld={setFld} />}
                  {tab === 'imaging' && <StudiesTab cur={cur} patch={patch} shiftCode={shiftCode} />}
                  {tab === 'notes'   && <NotesTab   cur={cur} patch={patch} shiftCode={shiftCode} />}
                  {tab === 'refer'   && <ReferTab cur={cur} patch={patch} shiftCode={shiftCode} />}
                  {tab === 'discharge' && <DischargeTab cur={cur} patch={patch} setFld={setFld} onCopy={() => { const text = makeSummary([cur]); navigator.clipboard.writeText(text); }} />}
                </DesktopContent>
              </div>
            )}
          </DesktopMain>
        {overlaysJSX}
        </DesktopShell>
      );
    }

    // ── DESKTOP: Add view ──
    if (view === 'add') {
      const np = newPt;
      const nset = (field, val) => setNewPt(p => ({ ...p, [field]: val }));
      return (
        <DesktopShell>
          {teamModalJSX}
      {subspecModalJSX}
        {subspecModalJSX}
  <div style={{ width: '35vw', minWidth: '300px', maxWidth: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: C.bg1, borderRight: `1px solid ${C.bd}` }}>
          {sidebarJSX}
        </div>
          <DesktopMain>
            <DesktopTopBar title="New Patient"
              right={<>
                <button onClick={addPt} style={{ background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', borderRadius: 8, padding: '6px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>✓ Add Patient</button>
                <button onClick={() => setView('list')} style={{ background: C.bg2, color: C.dim, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              </>}
            />
            <DesktopContent>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 900 }}>
                <div>
                  <Card>
                    <SLabel>Patient Info</SLabel>
                    <IFld label="Full Name" value={np.name} onChange={v => nset('name', v)} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                      <IFld label="MRN"  value={np.mrn}  onChange={v => nset('mrn',  v)} />
                      <IFld label="Room" value={np.room} onChange={v => nset('room', v)} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Birth</div>
                      <input type="date" value={np.dob || ''} onChange={e => { const d = e.target.value; nset('dob', d); nset('age', calcAge(d)); }}
                        style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: np.dob ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
                      {np.dob && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>Age: {calcAge(np.dob)} years old</div>}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Weight</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input value={np.weight || ''} onChange={e => nset('weight', e.target.value)} placeholder="e.g. 70"
                          style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
                        <select value={np.weightUnit || 'kg'} onChange={e => nset('weightUnit', e.target.value)}
                          style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 8px', color: C.dim, fontSize: 12, flexShrink: 0 }}>
                          <option value="kg">kg</option><option value="lbs">lbs</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Sex</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['M', 'F', 'Other'].map(s => (
                          <button key={s} onClick={() => nset('sex', s)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.sex === s ? '#4A9EFF' : C.bd}`, background: np.sex === s ? '#1A3D6A' : 'transparent', color: np.sex === s ? '#7DC4FF' : C.dim, cursor: 'pointer', fontSize: 13 }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <IFld label="Consultant" value={np.consultant || ''} onChange={v => nset('consultant', v)} />
                    <div style={{ marginBottom: 12, overflow: 'hidden' }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Admission</div>
                      <input type="date" value={np.admissionDate || ''} onChange={e => nset('admissionDate', e.target.value)}
                        style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: np.admissionDate ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
                      {np.admissionDate && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>D{hospDay(np.admissionDate)} (admitted {new Date(np.admissionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })})</div>}
                    </div>
                  </Card>
                </div>
                <div>
                  <Card>
                    <SLabel>Team</SLabel>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {teams.map(t => (
                        <button key={t.id} onClick={() => nset('team', t.id)}
                          style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.team === t.id ? t.color : C.bd}`, background: np.team === t.id ? t.color + '22' : 'transparent', color: np.team === t.id ? t.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </Card>
                  {subspecs.length > 0 && (
                    <Card>
                      <SLabel>Subspec</SLabel>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => nset('subspec', '')}
                          style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${!np.subspec ? '#C8DFF0' : C.bd}`, background: !np.subspec ? '#C8DFF022' : 'transparent', color: !np.subspec ? '#C8DFF0' : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          None
                        </button>
                        {subspecs.map(s => (
                          <button key={s.id} onClick={() => nset('subspec', s.id)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.subspec === s.id ? s.color : C.bd}`, background: np.subspec === s.id ? s.color + '22' : 'transparent', color: np.subspec === s.id ? s.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {s.name}
                          </button>
                        ))}
                      </div>
                    </Card>
                  )}
                  <Card>
                    <SLabel>Status</SLabel>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 6 }}>Disposition</div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                      {Object.entries(URG).map(([k, u]) => (
                        <button key={k} onClick={() => nset('urgency', k)}
                          style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${np.urgency === k ? u.color : C.bd}`, background: np.urgency === k ? u.bg : 'transparent', color: np.urgency === k ? u.color : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                          {u.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 6 }}>Service</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {CLIST.map(c => { const cc = CCOL[c] || '#AAA'; return (
                        <button key={c} onClick={() => nset('code', c)}
                          style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${np.code === c ? cc : C.bd}`, background: np.code === c ? cc + '22' : 'transparent', color: np.code === c ? cc : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                          {c}
                        </button>
                      ); })}
                    </div>
                  </Card>
                  <Card>
                    <SLabel>Clinical</SLabel>
                    <IFld label="Primary Diagnosis" value={np.diagnosis} onChange={v => nset('diagnosis', v)} />
                    <IFld label="Allergies"         value={np.allergies} onChange={v => nset('allergies', v)} />
                  </Card>
                </div>
              </div>
            </DesktopContent>
          </DesktopMain>
        {overlaysJSX}
        </DesktopShell>
      );
    }

    // fallback for other desktop views — render mobile
  }

  // ════════════════════════════════════════════
  // LIST VIEW (mobile)
  if (view === 'list') return (
    <AppShell>
      {teamModalJSX}
      {subspecModalJSX}
      <TopBar title="🏥 Patient Tracker"
        right={<div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
          <ModeToggle viewMode={viewMode} setVM={setVM} />
          <button onClick={() => setShowAllTasks(true)} title="All tasks"
            style={{ background: '#1A0A2A', color: '#BF5AF2', border: '1px solid #3A1A5A', borderRadius: 7, padding: '0 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
            ✓ {pts.filter(p => p.urgency !== 'discharged' && p.urgency !== 'mortality' && (atTeamFilter === 'all' || p.team === atTeamFilter) && (atSsFilter === 'all' || p.subspec === atSsFilter) && (atSvcFilter === 'all' || p.code === atSvcFilter)).reduce((n, p) => n + (p.todos || []).filter(t => (t.status || 'Pending') !== 'Done' && !t.done).length, 0) || ''}
          </button>
          <button onClick={() => setView('summary')} style={{ background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', borderRadius: 7, padding: '0 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>Summary</button>
          <MoreMenu {...mmp} direction="down" btnStyle={{ padding: '0 9px', borderRadius: 7, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', alignSelf: 'stretch' }} />
        </div>}
      />
      <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
      <Ledger pts={pts} tf={tf} />

      {/* Team + Service filters — collapsible */}
      <div style={{ borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
        {/* Collapsed summary bar — always visible */}
        <div onClick={() => setFiltersOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Filters</span>
          <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {tf !== 'all' && (() => { const t = getTeam(tf); return t ? <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.color + '22', border: `1px solid ${t.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{t.name}</span> : null; })()}
            {ssFilter !== 'all' && (() => { const s = subspecs.find(x => x.id === ssFilter); return s ? <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.color + '22', border: `1px solid ${s.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{s.name}</span> : null; })()}
            {svcFilter !== 'all' && <span style={{ fontSize: 10, fontWeight: 700, color: CCOL[svcFilter] || '#AAA', background: (CCOL[svcFilter] || '#AAA') + '22', border: `1px solid ${(CCOL[svcFilter] || '#AAA')}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{svcFilter}</span>}
            {tf === 'all' && svcFilter === 'all' && ssFilter === 'all' && <span style={{ fontSize: 10, color: C.dim2 }}>All teams · All subspecs · All services</span>}
          </div>
          <span style={{ fontSize: 10, color: C.dim2, flexShrink: 0, transition: 'transform 0.2s', display: 'inline-block', transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>

        {/* Expanded filters */}
        {filtersOpen && (
          <>
            <div className="hscroll-safe" style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 5, alignItems: 'center' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Team</div>
              {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => {
                const cnt = t.id === 'all' ? 0 : pts.filter(p => p.team === t.id).length;
                return (
                  <button key={t.id} onClick={() => setTf(t.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${tf === t.id ? t.color : C.bd}`, background: tf === t.id ? t.color + '22' : C.bg2, color: tf === t.id ? t.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <span>{t.name}</span>
                    {cnt > 0 && <span style={{ background: tf === t.id ? t.color + '33' : C.bd, borderRadius: 10, padding: '0 5px', fontSize: 10, color: tf === t.id ? t.color : C.dim }}>{cnt}</span>}
                  </button>
                );
              })}
              <button onClick={() => setShowTM(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>
                ⚙ Teams
              </button>
            </div>
            <div className="hscroll-safe" style={{ display: 'flex', gap: 5, paddingBottom: 5, overflowX: 'auto' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Subspec</div>
              {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...subspecs.map(s => ({ ...s, label: s.name }))].map(opt => {
                const cnt = opt.id === 'all' ? 0 : pts.filter(p => (tf === 'all' || p.team === tf) && p.subspec === opt.id).length;
                const active = ssFilter === opt.id;
                return (
                  <button key={opt.id} onClick={() => setSsFilter(opt.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {opt.label}
                    {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                  </button>
                );
              })}
              <button onClick={() => setShowSM(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>⚙ Edit</button>
            </div>
            <div className="hscroll-safe" style={{ display: 'flex', gap: 5, paddingBottom: 8, overflowX: 'auto' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Service</div>
              {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => {
                const cnt = opt.id === 'all' ? 0 : pts.filter(p => (tf === 'all' || p.team === tf) && p.code === opt.id).length;
                const active = svcFilter === opt.id;
                return (
                  <button key={opt.id} onClick={() => setSvcFilter(opt.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {opt.label}
                    {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Urgency counts — clickable filters */}
      <div className="tally-grid" style={{ display: 'flex', gap: 5, padding: '6px 14px', flexShrink: 0 }}>
        {Object.entries(URG).map(([k, u]) => {
          const n = preDisp.filter(p => p.urgency === k).length;
          const active = dispFilter === k;
          return (
            <button key={k} onClick={() => setDispFilter(active ? 'all' : k)}
              style={{ flex: 1, background: active ? u.bg : C.bg2, border: `1px solid ${active ? u.color : u.color + '33'}`, borderRadius: 8, padding: '6px 4px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: u.color }}>{n}</div>
              <div className="tally-label" style={{ fontSize: 8, color: u.color + '99', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
            </button>
          );
        })}
      </div>

      {/* Search + Add Patient — inline below tally grid */}
      <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.bd}`, flexShrink: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patients…"
          style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '7px 11px', color: C.tx, fontSize: 12, outline: 'none', minWidth: 0 }} />
        <button onClick={() => setDense(!denseMode)} title={denseMode ? 'Card view' : 'Dense view'}
          style={{ background: denseMode ? '#1A3D6A' : C.bg2, color: denseMode ? '#7DC4FF' : C.dim, border: '1px solid ' + (denseMode ? '#4A9EFF' : C.bd), borderRadius: 8, padding: '7px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
          {denseMode ? '☰' : '▤'}
        </button>
        <button onClick={() => { setNewPt(blankPt(tf !== 'all' ? tf : '')); setView('add'); }}
          style={{ width: 34, height: 34, borderRadius: '50%', background: '#1A3D6A', color: '#7DC4FF', border: '2px solid #4A9EFF', fontSize: 22, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 10px rgba(74,158,255,0.4)', lineHeight: 1 }}>+</button>
      </div>

      {/* Patient list */}
      <div className="scroll-safe" style={{ flex: 1, overflowY: 'auto', padding: '0 14px 8px' }}>
        {sync === 'loading' && <div style={{ textAlign: 'center', color: C.dim, padding: '40px 0', fontSize: 13 }}>Loading patients…</div>}
        {sync !== 'loading' && filtered.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: C.dim, fontSize: 13 }}>No patients found</div>}
        {filtered.map(p => {
          const u = URG[p.urgency], tm = getTeam(p.team), pend = (p.todos || []).filter(t => !t.done).length;
          const urgentTasks = (p.todos || []).filter(t => !t.done && (t.priority || 'routine') === 'urgent').length;

          // Change highlighting since last login
          const lastSaved = p.lastModified || 0;
          const isNew = lastSaved > lastLoginAt && lastLoginAt > 0;

          // Escalation timer
          const lastRev = p.lastReviewed;
          const staleThresh = p.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT;
          const admittedAt = p.admissionDate ? new Date(p.admissionDate).getTime() : 0;
          const isStale = lastRev ? (Date.now() - lastRev > staleThresh) : (admittedAt > 0 && Date.now() - admittedAt > staleThresh);
          const revMins = lastRev ? Math.floor((Date.now() - lastRev) / 60000) : null;

          if (denseMode) {
            return (
              <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 7, background: isNew ? '#0A1E12' : C.bg3, border: '1px solid ' + (isStale ? '#FF450044' : isNew ? '#30D15844' : C.bd), borderLeft: '3px solid ' + u.color, borderRadius: 7, padding: '6px 10px', marginBottom: 3, cursor: 'pointer' }}>
                <span style={{ fontSize: 10, color: C.dim, width: 28, flexShrink: 0, fontWeight: 700 }}>{p.room || '?'}</span>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#E0EEFA', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || 'Unnamed'}</span>
                <span style={{ fontSize: 10, color: '#6A9BBC', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.diagnosis || ''}</span>
                {urgentTasks > 0 && <span style={{ fontSize: 9, fontWeight: 900, color: '#FF4545', background: 'rgba(255,69,69,0.15)', border: '1px solid #FF454544', borderRadius: 4, padding: '1px 4px', flexShrink: 0 }}>{urgentTasks}!</span>}
                {pend > 0 && !urgentTasks && <span style={{ fontSize: 9, color: '#FF9500', flexShrink: 0 }}>⏳{pend}</span>}
                {isStale && <span style={{ fontSize: 9, color: '#FF4545', flexShrink: 0 }}>⚠{lastRev ? (revMins >= 60 ? Math.floor(revMins / 60) + 'h' : revMins + 'm') : '!'}</span>}
                {isNew && !isStale && <span style={{ fontSize: 8, fontWeight: 900, color: '#30D158', background: '#30D15822', borderRadius: 4, padding: '1px 4px', flexShrink: 0 }}>NEW</span>}
                <button onClick={e => { e.stopPropagation(); markReviewed(p.id); }}
                  style={{ background: !isStale ? '#081A0C' : 'none', border: '1px solid ' + (!isStale ? '#1A3A1E' : C.bd), borderRadius: 5, color: !isStale ? '#30D158' : C.dim2, fontSize: 9, padding: '2px 5px', cursor: 'pointer', flexShrink: 0 }}>✓</button>
              </div>
            );
          }

          return (
            <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
              style={{ background: isNew ? '#0A1E12' : C.bg3, border: '1px solid ' + (isStale ? '#FF950044' : isNew ? '#30D15844' : C.bd), borderLeft: '3px solid ' + u.color, borderRadius: 9, padding: '9px 12px', marginBottom: 6, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA', flex: 1 }}>{p.name || 'Unnamed'}</span>

                {(() => { const ro = RESUS_OPTIONS.find(r => r.k === (p.resus || 'full')); return ro && ro.k !== 'full' ? <span style={{ fontSize: 8, fontWeight: 900, color: ro.color, background: ro.bg, border: '1px solid ' + ro.color + '66', borderRadius: 4, padding: '1px 4px', flexShrink: 0 }}>{ro.short}</span> : null; })()}<UBadge u={p.urgency} /><CBadge code={p.code} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: C.dim }}>Rm {p.room}</span>
                <span style={{ fontSize: 10, color: C.dim }}>{p.age}{p.sex}</span>
                {p.admissionDate && hospDay(p.admissionDate) !== null && <span style={{ fontSize: 10, color: '#A8C8A0', fontWeight: 700 }}>D{hospDay(p.admissionDate)}</span>}
                {p.consultant && <span style={{ fontSize: 10, color: '#7DC4FF' }}>👤 {p.consultant}</span>}
                {tm && tf === 'all' && <span style={{ fontSize: 9, fontWeight: 700, color: tm.color, background: tm.color + '22', padding: '1px 5px', borderRadius: 4 }}>{tm.name}</span>}
              </div>
              <div style={{ fontSize: 11, color: '#6A9BBC', marginBottom: (pend || urgentTasks) ? 5 : 0 }}>{p.diagnosis || 'No diagnosis'}</div>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                {urgentTasks > 0 && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(255,69,69,0.12)', border: '1px solid #FF454544', borderRadius: 5, padding: '1px 7px', fontSize: 10, color: '#FF4545', fontWeight: 700 }}>🚨 {urgentTasks} urgent</div>}
                {pend > 0 && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#FF950015', border: '1px solid #FF950044', borderRadius: 5, padding: '1px 7px', fontSize: 10, color: '#FF9500', fontWeight: 600 }}>⏳ {pend}</div>}
                {isStale
                  ? <span style={{ fontSize: 9, color: '#FF4545' }}>⚠ {lastRev ? (revMins >= 60 ? Math.floor(revMins / 60) + 'h' : revMins + 'm') + ' ago' : 'Not reviewed'}</span>
                  : lastRev && <span style={{ fontSize: 9, color: '#30D15888' }}>✓ {revMins < 60 ? revMins + 'm' : Math.floor(revMins / 60) + 'h'} ago</span>}
                <button onClick={e => { e.stopPropagation(); markReviewed(p.id); }}
                  style={{ marginLeft: 'auto', background: !isStale ? '#081A0C' : 'none', border: '1px solid ' + (!isStale ? '#1A3A1E' : C.bd), borderRadius: 5, color: !isStale ? '#30D158' : C.dim2, fontSize: 10, padding: '2px 7px', cursor: 'pointer' }}>✓</button>
              </div>
            </div>
          );
        })}
      </div>

    {overlaysJSX}
    </AppShell>
  );

  // ════════════════════════════════════════════
  // DETAIL VIEW
  // ════════════════════════════════════════════
  if (view === 'detail' && cur) {
    const u = URG[cur.urgency], tm = getTeam(cur.team);
    const todos = [...(cur.todos || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const pend = todos.filter(t => !t.done).length;
    const flagged = (cur.labsLog || []).flatMap(g => g.entries.filter(e => e.flag));
    const tabs = [
      { k: 'issues',  lb: 'Issues' },
      { k: 'notes',   lb: 'Notes'   },
      { k: 'vitals',  lb: 'Vitals' },
      { k: 'meds',    lb: 'Meds' },
      { k: 'labs',    lb: 'Labs' },
      { k: 'imaging', lb: 'Studies' },
      { k: 'refer',   lb: 'Refer' },
      { k: 'tasks',   lb: 'Tasks' + (pend ? ' (' + pend + ')' : '') },
      ...(['mgh','discharged','mortality'].includes(cur.urgency) ? [{ k: 'discharge', lb: 'Discharge' }] : []),
    ];

    return (
      <AppShell>
        <TopBar left={<BackBtn onClick={() => setView('list')} />} title=""
          right={<div style={{ display: 'flex', gap: 5 }}>
            <ModeToggle viewMode={viewMode} setVM={setVM} />
            <button onClick={() => setShowWardRound(true)} title="Start Rounds"
              style={{ background: '#0A1A08', color: '#A8D878', border: '1px solid #2A4A14', borderRadius: 8, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>🔄</button>
            <button onClick={() => consultNotify(cur)} title="Send consultant update"
              style={{ background: '#0A1A2A', color: '#7DC4FF', border: '1px solid #2A4A6A', borderRadius: 8, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>📤</button>
            <button onClick={() => { shareOrCopy(makeFullBackup([cur])); }} title="Copy full patient backup"
              style={{ background: '#0A1A2A', color: '#64D2FF', border: '1px solid #1A3A4A', borderRadius: 8, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>💾</button>
            <button onClick={() => setView('edit')} style={{ background: C.bg2, color: C.dim, border: '1px solid ' + C.bd, borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
          </div>}
        />
        <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
        <div style={{ background: u.bg, borderBottom: `2px solid ${u.color}44`, padding: '10px 16px', flexShrink: 0 }}>
          {/* Row 1: CBadge · Team · NEWS2 · GCS — all same height */}
          {(() => {
            const bdg = { fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 5, lineHeight: 1, display: 'inline-flex', alignItems: 'center' };
            const lv  = [...(cur.vitalsLog||[])].sort((a,b) => (b.date+b.time).localeCompare(a.date+a.time))[0];
            const n2  = calcNEWS2(lv);
            const gcs = calcGCS(lv);
            return (
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                <CBadge code={cur.code} style={bdg} />
                {tm && <span style={{ ...bdg, color: tm.color, background: tm.color + '22', border: '1px solid ' + tm.color + '44' }}>{tm.name}</span>}
                {n2  && <span style={{ ...bdg, color: n2.color,  background: n2.bg,  border: '1px solid ' + n2.color  + '44', fontWeight: 900 }} title={n2.reasons.join(', ')}>NEWS2 {n2.score}</span>}
                {gcs && <span style={{ ...bdg, color: gcs.color, background: gcs.bg, border: '1px solid ' + gcs.color + '44', fontWeight: 900 }} title={`${gcs.risk} Brain Injury · E${gcs.e}V${gcs.v}M${gcs.m}`}>GCS {gcs.total}</span>}
              </div>
            );
          })()}
          {/* Row 2: Patient name */}
          <div style={{ fontSize: 17, fontWeight: 800, color: '#E8F4FF', marginBottom: 4 }}>{cur.name}</div>
          {/* Row 3: Room · age · weight · D · consultant */}
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Rm {cur.room} · {cur.age ? `${cur.age}yo ` : ''}{cur.sex}{cur.weight ? ` · ${cur.weight} ${cur.weightUnit || 'kg'}` : ''}{cur.admissionDate && hospDay(cur.admissionDate) ? ` · D${hospDay(cur.admissionDate)}` : ''}{cur.consultant ? ` · 👤 ${cur.consultant}` : ''}</div>
          {/* Row 4: Diagnosis */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#E0EEFA', flex: 1 }}>{cur.diagnosis || 'No diagnosis'}</div>
          </div>
          {/* Status / Resus / Subspec — collapsible pill groups */}
          {(() => {
            const urgObj   = URG[cur.urgency] || URG.stable;
            const resusObj = RESUS_OPTIONS.find(r => r.k === (cur.resus || 'full')) || RESUS_OPTIONS[0];
            const ssObj    = subspecs.find(s => s.id === cur.subspec);
            const ssBtnCol = ssObj ? (SSCOLS[ssObj.id] || '#7DC4FF') : C.dim2;
            const pill     = { padding: '3px 9px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', transition: 'all 0.1s' };
            const toggle   = (g) => setExpandGroup(prev => prev === g ? null : g);
            const limit    = cur.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT;
            const fresh    = cur.lastReviewed && (Date.now() - cur.lastReviewed) < limit;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {/* Summary row — one pill per group */}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => toggle('status')}
                    style={{ ...pill, border: `1px solid ${urgObj.color}`, background: expandGroup === 'status' ? urgObj.bg : 'transparent', color: urgObj.color }}>
                    {urgObj.label} {expandGroup === 'status' ? '▲' : '▾'}
                  </button>
                  <button onClick={() => toggle('resus')}
                    style={{ ...pill, border: `1px solid ${resusObj.color}`, background: expandGroup === 'resus' ? resusObj.bg : 'transparent', color: resusObj.color }}>
                    {resusObj.label} {expandGroup === 'resus' ? '▲' : '▾'}
                  </button>
                  {subspecs.length > 0 && (
                    <button onClick={() => toggle('subspec')}
                      style={{ ...pill, border: `1px solid ${ssBtnCol}`, background: expandGroup === 'subspec' ? ssBtnCol + '22' : 'transparent', color: ssBtnCol }}>
                      {ssObj ? ssObj.name : 'No Subspec'} {expandGroup === 'subspec' ? '▲' : '▾'}
                    </button>
                  )}
                  <button onClick={() => markReviewed(selId)}
                    style={{ ...pill, marginLeft: 'auto', background: fresh ? '#081A0C' : 'none', color: fresh ? '#30D158' : C.dim, border: '1px solid ' + (fresh ? '#1A3A1E' : C.bd), fontWeight: fresh ? 700 : 400 }}>
                    ✓ Reviewed
                  </button>
                </div>
                {/* Expanded options */}
                {expandGroup === 'status' && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingLeft: 6, borderLeft: `2px solid ${C.bd}` }}>
                    {Object.entries(URG).map(([k, urg]) => (
                      <button key={k} onClick={() => { setFld('urgency', k); setExpandGroup(null); }}
                        style={{ ...pill, border: '1px solid ' + (cur.urgency === k ? urg.color : C.bd), background: cur.urgency === k ? urg.bg : 'transparent', color: cur.urgency === k ? urg.color : C.dim }}>
                        {urg.label}
                      </button>
                    ))}
                  </div>
                )}
                {expandGroup === 'resus' && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingLeft: 6, borderLeft: `2px solid ${C.bd}` }}>
                    {RESUS_OPTIONS.map(ro => (
                      <button key={ro.k} onClick={() => { setFld('resus', ro.k); setExpandGroup(null); }}
                        style={{ ...pill, border: '1px solid ' + ((cur.resus||'full') === ro.k ? ro.color : C.bd), background: (cur.resus||'full') === ro.k ? ro.bg : 'transparent', color: (cur.resus||'full') === ro.k ? ro.color : C.dim }}>
                        {ro.label}
                      </button>
                    ))}
                  </div>
                )}
                {expandGroup === 'subspec' && subspecs.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingLeft: 6, borderLeft: `2px solid ${C.bd}` }}>
                    <button onClick={() => { setFld('subspec', ''); setExpandGroup(null); }}
                      style={{ ...pill, border: '1px solid ' + (!cur.subspec ? '#4A9EFF' : C.bd), background: !cur.subspec ? '#1A3D6A' : 'transparent', color: !cur.subspec ? '#7DC4FF' : C.dim }}>
                      None
                    </button>
                    {subspecs.map(ss => {
                      const col = SSCOLS[ss.id] || '#7DC4FF';
                      return (
                        <button key={ss.id} onClick={() => { setFld('subspec', ss.id); setExpandGroup(null); }}
                          style={{ ...pill, border: '1px solid ' + (cur.subspec === ss.id ? col : C.bd), background: cur.subspec === ss.id ? col + '22' : 'transparent', color: cur.subspec === ss.id ? col : C.dim }}>
                          {ss.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.bd}`, background: C.bg0, flexShrink: 0 }}>
          {tabs.map(t => <button key={t.k} onClick={() => setTab(t.k)} style={tSt(t.k)}>{t.lb}</button>)}
        </div>
        <div className="scroll-safe" style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 80px' }}>
          {tab === 'issues' && <IssuesTab cur={cur} setFld={setFld} patch={patch} showResolved={showResolved} setShowResolved={setShowResolved} onNotify={(title, body) => sendPushNotif(title, body, cur)} />}
          {tab === 'vitals' && <VitalsTab cur={cur} vd={vd} setVd={setVd} saveVitals={saveVitals} delVitals={delVitals} getIODay={getIODay} addIOEntry={addIOEntry} editIOEntry={editIOEntry} delIOEntry={delIOEntry} delIODay={delIODay} />}
          {tab === 'tasks'  && <TasksTab todos={todos} addTodo={addTodo} togTodo={togTodo} editTodo={editTodo} delTodo={delTodo} moveTodo={moveTodo} dropTodo={dropTodo} dragIdx={dragIdx} setDragIdx={setDragIdx} dragOver={dragOver} setDragOver={setDragOver} onAddLabResult={() => setTab('labs')} onAddImagingResult={() => setTab('imaging')} />}
          {tab === 'labs'   && <LabsTab cur={cur} ldate={ldate} setLdate={setLdate} ltime={ltime} setLtime={setLtime} addLabSet={addLabSet} addLabRow={addLabRow} editLab={editLab} delLabRow={delLabRow} delLabSet={delLabSet} saveLabSet={saveLabSet} clearLabFlag={clearLabFlag} />}
          {tab === 'meds'    && <MedsTab    cur={cur} patch={patch} setFld={setFld} />}
          {tab === 'imaging' && <StudiesTab cur={cur} patch={patch} shiftCode={shiftCode} />}
          {tab === 'notes'   && <NotesTab   cur={cur} patch={patch} shiftCode={shiftCode} />}
          {tab === 'refer'   && <ReferTab cur={cur} patch={patch} shiftCode={shiftCode} />}
          {tab === 'discharge' && <DischargeTab cur={cur} patch={patch} setFld={setFld} onCopy={() => { const text = makeSummary([cur]); navigator.clipboard.writeText(text); }} />}
        </div>
      {overlaysJSX}
      </AppShell>
    );
  }

  // ════════════════════════════════════════════
  // EDIT VIEW
  // ════════════════════════════════════════════
  if (view === 'edit' && cur) return (
    <AppShell>
      <TopBar left={<BackBtn onClick={() => setView('detail')} />} title="Edit Patient"
        right={<div style={{ display: 'flex', gap: 5 }}><ModeToggle viewMode={viewMode} setVM={setVM} /><button onClick={() => setView('detail')} style={{ background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>✓ Done</button></div>}
      />
      <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 40px' }}>
        <Card>
          <SLabel>Patient Info</SLabel>
          <IFld label="Full Name" value={cur.name} onChange={v => setFld('name', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <IFld label="MRN"  value={cur.mrn}  onChange={v => setFld('mrn',  v)} />
            <IFld label="Room" value={cur.room} onChange={v => setFld('room', v)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Birth</div>
            <input type="date" value={cur.dob || ''} onChange={e => { const d = e.target.value; setFld('dob', d); setFld('age', calcAge(d)); }}
              style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: cur.dob ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
            {cur.dob && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>Age: {calcAge(cur.dob)} years old</div>}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Weight</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <input value={cur.weight || ''} onChange={e => setFld('weight', e.target.value)} placeholder="e.g. 70"
                style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
              <select value={cur.weightUnit || 'kg'} onChange={e => setFld('weightUnit', e.target.value)}
                style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 8px', color: C.dim, fontSize: 12, flexShrink: 0 }}>
                <option value="kg">kg</option><option value="lbs">lbs</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Sex</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['M', 'F', 'Other'].map(s => (
                <button key={s} onClick={() => setFld('sex', s)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.sex === s ? '#4A9EFF' : C.bd}`, background: cur.sex === s ? '#1A3D6A' : 'transparent', color: cur.sex === s ? '#7DC4FF' : C.dim, cursor: 'pointer', fontSize: 13 }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <IFld label="Consultant" value={cur.consultant || ''} onChange={v => setFld('consultant', v)} />
          <div style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Admission</div>
            <input type="date" value={cur.admissionDate || ''} onChange={e => setFld('admissionDate', e.target.value)}
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: cur.admissionDate ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
            {cur.admissionDate && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>D{hospDay(cur.admissionDate)} (admitted {new Date(cur.admissionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })})</div>}
          </div>
        </Card>

        <Card>
          <SLabel>Team</SLabel>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {teams.map(t => (
              <button key={t.id} onClick={() => setFld('team', t.id)}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.team === t.id ? t.color : C.bd}`, background: cur.team === t.id ? t.color + '22' : 'transparent', color: cur.team === t.id ? t.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {t.name}
              </button>
            ))}
          </div>
        </Card>
        {subspecs.length > 0 && (
          <Card>
            <SLabel>Subspec</SLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setFld('subspec', '')}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${!cur.subspec ? '#C8DFF0' : C.bd}`, background: !cur.subspec ? '#C8DFF022' : 'transparent', color: !cur.subspec ? '#C8DFF0' : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                None
              </button>
              {subspecs.map(s => (
                <button key={s.id} onClick={() => setFld('subspec', s.id)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${cur.subspec === s.id ? s.color : C.bd}`, background: cur.subspec === s.id ? s.color + '22' : 'transparent', color: cur.subspec === s.id ? s.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {s.name}
                </button>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <SLabel>Status</SLabel>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Disposition</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {Object.entries(URG).map(([k, u]) => (
                <button key={k} onClick={() => setFld('urgency', k)}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${cur.urgency === k ? u.color : C.bd}`, background: cur.urgency === k ? u.bg : 'transparent', color: cur.urgency === k ? u.color : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Service</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {CLIST.map(c => { const cc = CCOL[c] || '#AAA'; return (
              <button key={c} onClick={() => setFld('code', c)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${cur.code === c ? cc : C.bd}`, background: cur.code === c ? cc + '22' : 'transparent', color: cur.code === c ? cc : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {c}
              </button>
            ); })}
          </div>
        </Card>

        <Card>
          <SLabel>Clinical</SLabel>
          <IFld label="Primary Diagnosis" value={cur.diagnosis} onChange={v => setFld('diagnosis', v)} />
          <IFld label="Allergies"         value={cur.allergies} onChange={v => setFld('allergies', v)} />
        </Card>

        <Card s={{ background: '#0D1A10', border: '1px solid #1A3A1E' }}>
          <SLabel color="#30D158">Archive</SLabel>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Manually move this patient to the archive for future documentation and case review.</div>
          <WBtn onClick={() => {
            if (!window.confirm(`Archive ${cur.name || 'this patient'}? They will be removed from the active list.`)) return;
            manualArchive(cur);
          }} bg="#081A0C" color="#30D158" bdr="1px solid #1A3A1E">📦 Archive This Patient</WBtn>
        </Card>

        <Card s={{ background: '#1A0808', border: '1px solid #3A1515' }}>
          <SLabel color="#FF6B6B">Danger Zone</SLabel>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Permanently remove this patient from the active list without archiving. This cannot be undone.</div>
          <WBtn onClick={() => {
            if (!window.confirm('Remove ' + (cur.name || 'this patient') + '? This cannot be undone.')) return;
            if (!window.confirm('Are you sure? Patient will be permanently deleted.')) return;
            delPt(cur.id); setView('list');
          }} bg="#1A0808" color="#FF6B6B" bdr="1px solid #3A1515">🗑 Remove Patient</WBtn>
        </Card>
      </div>
    {overlaysJSX}
    </AppShell>
  );

  // ════════════════════════════════════════════
  // ADD VIEW
  // ════════════════════════════════════════════
  const np = newPt;
  const nset = (field, val) => setNewPt(p => ({ ...p, [field]: val }));
  if (view === 'add') return (
    <AppShell>
      <TopBar left={<BackBtn onClick={() => setView('list')} />} title="New Patient"
        right={<button onClick={addPt} style={{ background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>✓ Add</button>}
      />
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 40px' }}>

        {/* Patient Info */}
        <Card>
          <SLabel>Patient Info</SLabel>
          <IFld label="Full Name" value={np.name} onChange={v => nset('name', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <IFld label="MRN"  value={np.mrn}  onChange={v => nset('mrn',  v)} />
            <IFld label="Room" value={np.room} onChange={v => nset('room', v)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Birth</div>
            <input type="date" value={np.dob || ''} onChange={e => { const d = e.target.value; nset('dob', d); nset('age', calcAge(d)); }}
              style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: np.dob ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
            {np.dob && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>Age: {calcAge(np.dob)} years old</div>}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Weight</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <input value={np.weight || ''} onChange={e => nset('weight', e.target.value)} placeholder="e.g. 70"
                style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 }} />
              <select value={np.weightUnit || 'kg'} onChange={e => nset('weightUnit', e.target.value)}
                style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 8px', color: C.dim, fontSize: 12, flexShrink: 0 }}>
                <option value="kg">kg</option><option value="lbs">lbs</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Sex</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['M', 'F', 'Other'].map(s => (
                <button key={s} onClick={() => nset('sex', s)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.sex === s ? '#4A9EFF' : C.bd}`, background: np.sex === s ? '#1A3D6A' : 'transparent', color: np.sex === s ? '#7DC4FF' : C.dim, cursor: 'pointer', fontSize: 13 }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <IFld label="Consultant" value={np.consultant || ''} onChange={v => nset('consultant', v)} />
          <div style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Date of Admission</div>
            <input type="date" value={np.admissionDate || ''} onChange={e => nset('admissionDate', e.target.value)}
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: np.admissionDate ? C.tx : C.dim, fontSize: 13, colorScheme: 'dark' }} />
            {np.admissionDate && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>D{hospDay(np.admissionDate)} (admitted {new Date(np.admissionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })})</div>}
          </div>
        </Card>

        {/* Team */}
        <Card>
          <SLabel>Team</SLabel>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {teams.map(t => (
              <button key={t.id} onClick={() => nset('team', t.id)}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.team === t.id ? t.color : C.bd}`, background: np.team === t.id ? t.color + '22' : 'transparent', color: np.team === t.id ? t.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {t.name}
              </button>
            ))}
          </div>
        </Card>
        {subspecs.length > 0 && (
          <Card>
            <SLabel>Subspec</SLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => nset('subspec', '')}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${!np.subspec ? '#C8DFF0' : C.bd}`, background: !np.subspec ? '#C8DFF022' : 'transparent', color: !np.subspec ? '#C8DFF0' : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                None
              </button>
              {subspecs.map(s => (
                <button key={s.id} onClick={() => nset('subspec', s.id)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${np.subspec === s.id ? s.color : C.bd}`, background: np.subspec === s.id ? s.color + '22' : 'transparent', color: np.subspec === s.id ? s.color : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {s.name}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Status */}
        <Card>
          <SLabel>Status</SLabel>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Disposition</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {Object.entries(URG).map(([k, u]) => (
                <button key={k} onClick={() => nset('urgency', k)}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${np.urgency === k ? u.color : C.bd}`, background: np.urgency === k ? u.bg : 'transparent', color: np.urgency === k ? u.color : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>Service</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {CLIST.map(c => { const cc = CCOL[c] || '#AAA'; return (
              <button key={c} onClick={() => nset('code', c)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${np.code === c ? cc : C.bd}`, background: np.code === c ? cc + '22' : 'transparent', color: np.code === c ? cc : C.dim, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {c}
              </button>
            ); })}
          </div>
        </Card>

        {/* Clinical */}
        <Card>
          <SLabel>Clinical</SLabel>
          <IFld label="Primary Diagnosis" value={np.diagnosis} onChange={v => nset('diagnosis', v)} />
          <IFld label="Allergies"         value={np.allergies} onChange={v => nset('allergies', v)} />
        </Card>

        <WBtn onClick={addPt} mb={8}>Add Patient →</WBtn>
        <WBtn onClick={() => setView('list')} bg={C.bg2} color="#5A8AAA" bdr={`1px solid ${C.bd}`}>Cancel</WBtn>
      </div>
    {overlaysJSX}
    </AppShell>
  );

  // ════════════════════════════════════════════
  // SUMMARY VIEW
  // ════════════════════════════════════════════
  if (view === 'summary' && viewMode === 'desktop') {
    const summPts = pts.filter(p => (tf === 'all' || p.team === tf) && (summDisp === 'all' || p.urgency === summDisp) && (summSvc === 'all' || p.code === summSvc)).sort((a, b) => UORD[a.urgency] - UORD[b.urgency]);
    const summarySidebarJSX = (
      <DesktopSidebar width="100%" flex>
        {/* Title row — mirrors mobile TopBar */}
        <div className="topbar" style={{ borderBottom: `1px solid ${C.bd}`, background: C.bg0, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 2px', flexShrink: 0 }}>&#8592; Back</button>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#E8F4FF', flex: 1, textAlign: 'center' }}>Endorsement Summary</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
              <ModeToggle viewMode={viewMode} setVM={setVM} />
              <MoreMenu {...mmp} direction="down" btnStyle={{ padding: '5px 10px', borderRadius: 8, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 16, cursor: 'pointer', lineHeight: 1 }} />
            </div>
          </div>
        </div>
        <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
        <Ledger pts={pts} tf={tf} />
        <DesktopSidebarContent>
          <div style={{ padding: '0 0 10px' }}>
            {/* Team + Service filters — collapsible */}
            <div onClick={() => setSummFiltersOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', cursor: 'pointer', userSelect: 'none', borderBottom: `1px solid ${C.bd}` }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Filters</span>
              <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
                {tf !== 'all' && (() => { const t = getTeam(tf); return t ? <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.color + '22', border: `1px solid ${t.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{t.name}</span> : null; })()}
                {summSsFilter !== 'all' && (() => { const s = subspecs.find(x => x.id === summSsFilter); return s ? <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.color + '22', border: `1px solid ${s.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{s.name}</span> : null; })()}
                {summSvc !== 'all' && <span style={{ fontSize: 10, fontWeight: 700, color: CCOL[summSvc] || '#AAA', background: (CCOL[summSvc] || '#AAA') + '22', border: `1px solid ${(CCOL[summSvc] || '#AAA')}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{summSvc}</span>}
                {tf === 'all' && summSvc === 'all' && summSsFilter === 'all' && <span style={{ fontSize: 10, color: C.dim2 }}>All teams · All subspecs · All services</span>}
              </div>
              <span style={{ fontSize: 10, color: C.dim2, flexShrink: 0, display: 'inline-block', transform: summFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </div>
            {summFiltersOpen && (<>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 14px 5px', alignItems: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Team</div>
                {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => (
                  <button key={t.id} onClick={() => setTf(t.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${tf === t.id ? t.color : C.bd}`, background: tf === t.id ? t.color + '22' : C.bg2, color: tf === t.id ? t.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {t.name}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, padding: '0 14px 5px', overflowX: 'auto', alignItems: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Subspec</div>
                {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...subspecs.map(s => ({ ...s, label: s.name }))].map(opt => {
                  const active = summSsFilter === opt.id;
                  return (
                    <button key={opt.id} onClick={() => setSummSsFilter(opt.id)}
                      style={{ padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {opt.label || opt.name}
                    </button>
                  );
                })}
                <button onClick={() => setShowSM(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>⚙ Edit</button>
              </div>
              <div style={{ display: 'flex', gap: 5, padding: '0 14px 8px', overflowX: 'auto', alignItems: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Service</div>
                {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => (
                  <button key={opt.id} onClick={() => setSummSvc(opt.id)}
                    style={{ padding: '3px 7px', borderRadius: 6, border: `1px solid ${summSvc === opt.id ? opt.color : C.bd}`, background: summSvc === opt.id ? opt.color + '22' : C.bg2, color: summSvc === opt.id ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </>)}
            {/* Disposition tally */}
            <div style={{ display: 'flex', gap: 5, padding: '8px 14px 6px', marginBottom: 0 }}>
              {Object.entries(URG).map(([k, u]) => {
                const pool = pts.filter(p => (tf === 'all' || p.team === tf) && (summSvc === 'all' || p.code === summSvc) && (summSsFilter === 'all' || p.subspec === summSsFilter));
                const n = pool.filter(p => p.urgency === k).length;
                const active = summDisp === k;
                return (
                  <button key={k} onClick={() => setSummDisp(active ? 'all' : k)}
                    style={{ flex: 1, background: active ? u.bg : C.bg2, border: `1px solid ${active ? u.color : u.color + '33'}`, borderRadius: 8, padding: '6px 4px', textAlign: 'center', cursor: 'pointer', minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: u.color }}>{n}</div>
                    <div style={{ fontSize: 7, color: u.color + '99', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </DesktopSidebarContent>
      </DesktopSidebar>
    );
    return (
      <DesktopShell>
<div style={{ width: '35vw', minWidth: '300px', maxWidth: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: C.bg1, borderRight: `1px solid ${C.bd}` }}>
          {summarySidebarJSX}
        </div>
        <DesktopMain>
          <DesktopTopBar title="Endorsement Summary" right={<span style={{ fontSize: 10, color: C.dim }}>{summPts.length} patients</span>} />
          <DesktopContent>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 14 }}>
              {summPts.map(p => {
                const u = URG[p.urgency], tm = getTeam(p.team), pend = (p.todos || []).filter(t => !t.done);
                const lastSaved = p.lastModified || 0;
                const isNew = lastSaved > lastLoginAt && lastLoginAt > 0;
                const lastRev = p.lastReviewed;
                const staleThresh = p.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT;
                const isStale = lastRev ? (Date.now() - lastRev > staleThresh) : ((p.admissionDate ? new Date(p.admissionDate).getTime() : 0) > 0 && Date.now() - new Date(p.admissionDate).getTime() > staleThresh);
                const revMins = lastRev ? Math.floor((Date.now() - lastRev) / 60000) : null;
                return (
                  <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
                    style={{ background: C.bg3, border: `1px solid ${isStale ? '#FF450044' : isNew ? '#30D15844' : C.bd}`, borderLeft: `3px solid ${u.color}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                      {(() => { const ro = RESUS_OPTIONS.find(r => r.k === (p.resus||'full')); return ro && ro.k !== 'full' ? <span style={{ fontSize: 8, fontWeight: 900, color: ro.color, background: ro.bg, border: '1px solid ' + ro.color + '66', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{ro.short}</span> : null; })()}<UBadge u={p.urgency} /><CBadge code={p.code} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA', flex: 1 }}>{p.name}</span>
                      {isNew && <span style={{ fontSize: 8, fontWeight: 900, color: '#30D158', background: '#30D15822', border: '1px solid #30D15844', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>UPDATED</span>}
                      {tm && <span style={{ fontSize: 9, fontWeight: 700, color: tm.color, background: tm.color + '22', padding: '1px 6px', borderRadius: 4 }}>{tm.name}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 3 }}>
                      <span style={{ fontSize: 11, color: C.dim }}>Rm {p.room}</span>
                      <span style={{ fontSize: 11, color: C.dim }}>{p.age}{p.sex}</span>
                      {p.weight && <span style={{ fontSize: 11, color: C.dim }}>{p.weight}{p.weightUnit || 'kg'}</span>}
                      {p.admissionDate && hospDay(p.admissionDate) !== null && <span style={{ fontSize: 11, color: '#A8C8A0', fontWeight: 700 }}>D{hospDay(p.admissionDate)}</span>}
                      {p.consultant && <span style={{ fontSize: 11, color: '#7DC4FF' }}>👤 {p.consultant}</span>}
                      {isStale
                        ? <span style={{ fontSize: 10, color: '#FF4545' }}>⚠ {lastRev ? (revMins >= 60 ? Math.floor(revMins / 60) + 'h' : revMins + 'm') + ' since review' : 'Not reviewed'}</span>
                        : lastRev && <span style={{ fontSize: 10, color: '#30D15888' }}>✓ {revMins < 60 ? revMins + 'm' : Math.floor(revMins / 60) + 'h'} ago</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#6A9BBC', marginBottom: 4 }}>{p.diagnosis}</div>
                    <div style={{ background: '#FF950010', border: `1px solid ${addingTask === p.id ? '#FF950066' : '#FF950030'}`, borderRadius: 8, padding: '8px 10px', marginBottom: 4 }}
                      onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: pend.length > 0 ? 4 : 0 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#FF9500', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                          ⏳ Pending{pend.length > 0 ? ` (${pend.length})` : ''}
                        </div>
                        <button onClick={e => { e.stopPropagation(); setAddingTask(addingTask === p.id ? null : p.id); setNewTaskText(''); setNewTaskType('general'); }}
                          style={{ background: addingTask === p.id ? '#FF950022' : 'transparent', border: `1px solid ${addingTask === p.id ? '#FF9500' : '#FF950044'}`, borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#FF9500', cursor: 'pointer', fontWeight: 700, lineHeight: 1.4 }}>
                          {addingTask === p.id ? '✕' : '+ Add'}
                        </button>
                      </div>
                      {[...(p.todos || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(t => (
                        <div key={t.id} onClick={e => { e.stopPropagation(); const nd = !(t.done || t.status === 'Done'); save(pts.map(pt => pt.id === p.id ? { ...pt, todos: pt.todos.map(td => td.id === t.id ? { ...td, done: nd, status: nd ? 'Done' : 'Pending' } : td) } : pt)); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #FF950015', cursor: 'pointer' }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${(t.done || t.status === 'Done') ? '#30D158' : '#FF950066'}`, background: (t.done || t.status === 'Done') ? '#30D15822' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {(t.done || t.status === 'Done') && <span style={{ fontSize: 8, color: '#30D158' }}>✓</span>}
                          </div>
                          <span style={{ fontSize: 12, color: (t.done || t.status === 'Done') ? '#2A5A3A' : '#CCAA70', lineHeight: 1.4, textDecoration: (t.done || t.status === 'Done') ? 'line-through' : 'none', flex: 1 }}>{t.text}</span>
                          {t.type && t.type !== 'general' && (
                            <span style={{ fontSize: 9, fontWeight: 800, color: t.type === 'labs' ? '#FF9500' : '#64D2FF', background: (t.type === 'labs' ? '#FF9500' : '#64D2FF') + '22', border: `1px solid ${(t.type === 'labs' ? '#FF9500' : '#64D2FF')}44`, borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                              {t.type === 'labs' ? '🧪' : '🩻'} {t.type.toUpperCase()}
                            </span>
                          )}
                        </div>
                      ))}
                      {addingTask === p.id && (
                        <div style={{ marginTop: 8, borderTop: '1px solid #FF950022', paddingTop: 8 }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 5, marginBottom: 7 }}>
                            {[{ k: 'general', label: '📋 General', color: '#C8DFF0' }, { k: 'labs', label: '🧪 Labs', color: '#FF9500' }, { k: 'imaging', label: '🩻 Studies', color: '#64D2FF' }].map(tp => (
                              <button key={tp.k} onClick={e => { e.stopPropagation(); setNewTaskType(tp.k); }}
                                style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: `1px solid ${newTaskType === tp.k ? tp.color : tp.color + '33'}`, background: newTaskType === tp.k ? tp.color + '22' : 'transparent', color: newTaskType === tp.k ? tp.color : tp.color + '55', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                {tp.label}
                              </button>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input autoFocus value={newTaskText} onChange={e => setNewTaskText(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && newTaskText.trim()) {
                                  const nt = { id: `t${Date.now()}`, text: newTaskText.trim(), done: false, order: (p.todos || []).length, type: newTaskType, addedAt: Date.now() };
                                  save(pts.map(pt => pt.id === p.id ? { ...pt, todos: [...(pt.todos || []), nt] } : pt));
                                  setNewTaskText(''); setNewTaskType('general'); setAddingTask(null);
                                }
                                if (e.key === 'Escape') { setAddingTask(null); setNewTaskText(''); setNewTaskType('general'); }
                              }}
                              placeholder="New task…"
                              style={{ flex: 1, background: '#1A0E00', border: '1px solid #FF950066', borderRadius: 7, padding: '6px 10px', color: '#FFD580', fontSize: 12, outline: 'none' }}
                            />
                            <button onClick={e => { e.stopPropagation(); if (!newTaskText.trim()) return; const nt = { id: `t${Date.now()}`, text: newTaskText.trim(), done: false, order: (p.todos || []).length, type: newTaskType, addedAt: Date.now() }; save(pts.map(pt => pt.id === p.id ? { ...pt, todos: [...(pt.todos || []), nt] } : pt)); setNewTaskText(''); setNewTaskType('general'); setAddingTask(null); }}
                              style={{ background: '#FF9500', border: 'none', borderRadius: 7, padding: '6px 14px', color: '#000', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>✓</button>
                          </div>
                        </div>
                      )}
                    </div>
                    {p.note && <div style={{ fontSize: 12, color: C.dim, fontStyle: 'italic', marginTop: 4 }}>{p.note}</div>}
                    {(() => { const ch = get24hChanges(p); return ch.length > 0 ? (
                      <div style={{ marginTop: 8, background: '#0A1E0A', border: '1px solid #1A3A1A', borderRadius: 8, padding: '8px 10px' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#30D158', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>⚡ Last 24h</div>
                        {ch.map((item, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 3 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: item.color, flexShrink: 0, minWidth: 70 }}>{item.label}</span>
                            <span style={{ fontSize: 11, color: '#C8DFF0', lineHeight: 1.4 }}>{item.text}</span>
                          </div>
                        ))}
                      </div>
                    ) : null; })()}
                  </div>
                );
              })}
            </div>
          </DesktopContent>
        </DesktopMain>
      {overlaysJSX}
      </DesktopShell>
    );
  }

  if (view === 'summary') return (
    <AppShell>
      <TopBar left={<BackBtn onClick={() => setView('list')} />} title="Endorsement Summary"
        right={<div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <ModeToggle viewMode={viewMode} setVM={setVM} />
          <MoreMenu {...mmp} direction="down" btnStyle={{ padding: '5px 10px', borderRadius: 8, background: C.bg2, border: '1px solid ' + C.bd, color: C.dim, fontSize: 16, cursor: 'pointer', lineHeight: 1 }} />
        </div>}
      />
      <SyncBar status={sync} meta={meta} onRefresh={load} shiftCode={shiftCode} />
      <Ledger pts={pts} tf={tf} />
      {/* Team + Service filters — collapsible */}
      <div style={{ borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
        <div onClick={() => setSummFiltersOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Filters</span>
          <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {tf !== 'all' && (() => { const t = getTeam(tf); return t ? <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.color + '22', border: `1px solid ${t.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{t.name}</span> : null; })()}
            {summSsFilter !== 'all' && (() => { const s = subspecs.find(x => x.id === summSsFilter); return s ? <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.color + '22', border: `1px solid ${s.color}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{s.name}</span> : null; })()}
            {summSvc !== 'all' && <span style={{ fontSize: 10, fontWeight: 700, color: CCOL[summSvc] || '#AAA', background: (CCOL[summSvc] || '#AAA') + '22', border: `1px solid ${(CCOL[summSvc] || '#AAA')}44`, borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>{summSvc}</span>}
            {tf === 'all' && summSvc === 'all' && summSsFilter === 'all' && <span style={{ fontSize: 10, color: C.dim2 }}>All teams · All subspecs · All services</span>}
          </div>
          <span style={{ fontSize: 10, color: C.dim2, flexShrink: 0, display: 'inline-block', transform: summFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </div>
        {summFiltersOpen && (<>
          <div className="hscroll-safe" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 7, alignItems: 'center' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingRight: 2 }}>Team</div>
            {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(t => {
              const cnt = t.id === 'all' ? 0 : pts.filter(p => p.team === t.id).length;
              return (
                <button key={t.id} onClick={() => setTf(t.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${tf === t.id ? t.color : C.bd}`, background: tf === t.id ? t.color + '22' : C.bg2, color: tf === t.id ? t.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  <span>{t.name}</span>
                  {cnt > 0 && <span style={{ background: C.bd, borderRadius: 10, padding: '0 5px', fontSize: 10 }}>{cnt}</span>}
                </button>
              );
            })}
          </div>
          <div className="hscroll-safe" style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 5 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Subspec</div>
            {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...subspecs.map(s => ({ ...s, label: s.name }))].map(opt => {
              const cnt = opt.id === 'all' ? 0 : pts.filter(p => (tf === 'all' || p.team === tf) && p.subspec === opt.id).length;
              const active = summSsFilter === opt.id;
              return (
                <button key={opt.id} onClick={() => setSummSsFilter(opt.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {opt.name || opt.label}
                  {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                </button>
              );
            })}
            <button onClick={() => setShowSM(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 7, border: `1px solid ${C.bd}`, background: C.bg2, color: C.dim, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>⚙ Edit</button>
          </div>
          <div className="hscroll-safe" style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 8 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Service</div>
            {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt => {
              const base = tf === 'all' ? pts : pts.filter(p => p.team === tf);
              const cnt = opt.id === 'all' ? 0 : base.filter(p => p.code === opt.id).length;
              const active = summSvc === opt.id;
              return (
                <button key={opt.id} onClick={() => setSummSvc(opt.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 6, border: `1px solid ${active ? opt.color : C.bd}`, background: active ? opt.color + '22' : C.bg2, color: active ? opt.color : C.dim, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {opt.label}
                  {cnt > 0 && <span style={{ fontSize: 9, background: active ? opt.color + '33' : C.bd, borderRadius: 8, padding: '0 4px', color: active ? opt.color : C.dim2 }}>{cnt}</span>}
                </button>
              );
            })}
          </div>
        </>)}
      </div>
      <div className="tally-grid" style={{ display: 'flex', gap: 5, padding: '8px 14px 4px', flexShrink: 0 }}>
        {Object.entries(URG).map(([k, u]) => {
          const pool = pts.filter(p => (tf === 'all' || p.team === tf) && (summSvc === 'all' || p.code === summSvc) && (summSsFilter === 'all' || p.subspec === summSsFilter));
          const n = pool.filter(p => p.urgency === k).length;
          const active = summDisp === k;
          return (
            <button key={k} onClick={() => setSummDisp(active ? 'all' : k)}
              style={{ flex: 1, background: active ? u.bg : C.bg2, border: `1px solid ${active ? u.color : u.color + '33'}`, borderRadius: 8, padding: '6px 4px', textAlign: 'center', cursor: 'pointer', minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: u.color }}>{n}</div>
              <div className="tally-label" style={{ fontSize: 8, color: u.color + '99', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
            </button>
          );
        })}
      </div>
      {meta && <div style={{ padding: '2px 14px 6px', textAlign: 'center', flexShrink: 0 }}><span style={{ fontSize: 10, color: C.dim }}>Last saved: {new Date(meta.saved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></div>}
      <div className="scroll-safe" style={{ flex: 1, overflowY: 'auto', padding: '0 14px 14px' }}>
        {pts.filter(p => (tf === 'all' || p.team === tf) && (summDisp === 'all' || p.urgency === summDisp) && (summSvc === 'all' || p.code === summSvc) && (summSsFilter === 'all' || p.subspec === summSsFilter)).sort((a, b) => UORD[a.urgency] - UORD[b.urgency]).map(p => {
          const u = URG[p.urgency], tm = getTeam(p.team), pend = (p.todos || []).filter(t => !t.done);
          return (
            <div key={p.id} onClick={() => { setSelId(p.id); setTab('issues'); setShowResolved(false); setView('detail'); }}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderLeft: `3px solid ${u.color}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                {(() => { const ro = RESUS_OPTIONS.find(r => r.k === (p.resus||'full')); return ro && ro.k !== 'full' ? <span style={{ fontSize: 8, fontWeight: 900, color: ro.color, background: ro.bg, border: '1px solid ' + ro.color + '66', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{ro.short}</span> : null; })()}<UBadge u={p.urgency} /><CBadge code={p.code} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA', flex: 1 }}>{p.name}</span>
                {tm && <span style={{ fontSize: 9, fontWeight: 700, color: tm.color, background: tm.color + '22', padding: '1px 6px', borderRadius: 4 }}>{tm.name}</span>}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: C.dim }}>Rm {p.room}</span>
                <span style={{ fontSize: 11, color: C.dim }}>{p.age}{p.sex}</span>
                {p.weight && <span style={{ fontSize: 11, color: C.dim }}>{p.weight}{p.weightUnit || 'kg'}</span>}
                {p.admissionDate && hospDay(p.admissionDate) !== null && <span style={{ fontSize: 11, color: '#A8C8A0', fontWeight: 700 }}>D{hospDay(p.admissionDate)}</span>}
                {p.consultant && <span style={{ fontSize: 11, color: '#7DC4FF' }}>👤 {p.consultant}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#6A9BBC', marginBottom: 4 }}>{p.diagnosis}</div>
              <div style={{ background: '#FF950010', border: `1px solid ${addingTask === p.id ? '#FF950066' : '#FF950030'}`, borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: pend.length > 0 ? 4 : 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: '#FF9500', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    ⏳ Pending{pend.length > 0 ? ` (${pend.length})` : ''}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); setAddingTask(addingTask === p.id ? null : p.id); setNewTaskText(''); setNewTaskType('general'); }}
                    style={{ background: addingTask === p.id ? '#FF950022' : 'transparent', border: `1px solid ${addingTask === p.id ? '#FF9500' : '#FF950044'}`, borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#FF9500', cursor: 'pointer', fontWeight: 700, lineHeight: 1.4 }}>
                    {addingTask === p.id ? '✕' : '+ Add'}
                  </button>
                </div>
                {[...(p.todos || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(t => (
                  <div key={t.id} onClick={e => { e.stopPropagation(); const nd = !(t.done || t.status === 'Done'); save(pts.map(pt => pt.id === p.id ? { ...pt, todos: pt.todos.map(td => td.id === t.id ? { ...td, done: nd, status: nd ? 'Done' : 'Pending' } : td) } : pt)); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #FF950015', cursor: 'pointer' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${(t.done || t.status === 'Done') ? '#30D158' : '#FF950066'}`, background: (t.done || t.status === 'Done') ? '#30D15822' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(t.done || t.status === 'Done') && <span style={{ fontSize: 8, color: '#30D158' }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 12, color: (t.done || t.status === 'Done') ? '#2A5A3A' : '#CCAA70', lineHeight: 1.4, textDecoration: (t.done || t.status === 'Done') ? 'line-through' : 'none', flex: 1 }}>{t.text}</span>
                    {t.type && t.type !== 'general' && (
                      <span style={{ fontSize: 9, fontWeight: 800, color: t.type === 'labs' ? '#FF9500' : '#64D2FF', background: (t.type === 'labs' ? '#FF9500' : '#64D2FF') + '22', border: `1px solid ${(t.type === 'labs' ? '#FF9500' : '#64D2FF')}44`, borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                        {t.type === 'labs' ? '🧪' : '🩻'} {t.type.toUpperCase()}
                      </span>
                    )}
                  </div>
                ))}
                {addingTask === p.id && (
                  <div style={{ marginTop: 8, borderTop: '1px solid #FF950022', paddingTop: 8 }} onClick={e => e.stopPropagation()}>
                    {/* Category pills */}
                    <div style={{ display: 'flex', gap: 5, marginBottom: 7 }}>
                      {[
                        { k: 'general', label: '📋 General', color: '#C8DFF0' },
                        { k: 'labs',    label: '🧪 Labs',    color: '#FF9500'  },
                        { k: 'imaging', label: '🩻 Studies', color: '#64D2FF'  },
                      ].map(tp => (
                        <button key={tp.k} onClick={e => { e.stopPropagation(); setNewTaskType(tp.k); }}
                          style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: `1px solid ${newTaskType === tp.k ? tp.color : tp.color + '33'}`, background: newTaskType === tp.k ? tp.color + '22' : 'transparent', color: newTaskType === tp.k ? tp.color : tp.color + '55', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.1s' }}>
                          {tp.label}
                        </button>
                      ))}
                    </div>
                    {/* Text input + confirm */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        autoFocus
                        value={newTaskText}
                        onChange={e => setNewTaskText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && newTaskText.trim()) {
                            const newTodo = { id: `t${Date.now()}`, text: newTaskText.trim(), done: false, order: (p.todos || []).length, type: newTaskType, addedAt: Date.now() };
                            save(pts.map(pt => pt.id === p.id ? { ...pt, todos: [...(pt.todos || []), newTodo] } : pt));
                            setNewTaskText(''); setNewTaskType('general'); setAddingTask(null);
                          }
                          if (e.key === 'Escape') { setAddingTask(null); setNewTaskText(''); setNewTaskType('general'); }
                        }}
                        placeholder="New task…"
                        style={{ flex: 1, background: '#1A0E00', border: '1px solid #FF950066', borderRadius: 7, padding: '6px 10px', color: '#FFD580', fontSize: 12, outline: 'none' }}
                      />
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          if (!newTaskText.trim()) return;
                          const newTodo = { id: `t${Date.now()}`, text: newTaskText.trim(), done: false, order: (p.todos || []).length, type: newTaskType, addedAt: Date.now() };
                          save(pts.map(pt => pt.id === p.id ? { ...pt, todos: [...(pt.todos || []), newTodo] } : pt));
                          setNewTaskText(''); setNewTaskType('general'); setAddingTask(null);
                        }}
                        style={{ background: '#FF9500', border: 'none', borderRadius: 7, padding: '6px 14px', color: '#000', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                        ✓
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {p.note && <div style={{ fontSize: 12, color: C.dim, fontStyle: 'italic' }}>{p.note}</div>}
              {(() => { const ch = get24hChanges(p); return ch.length > 0 ? (
                <div style={{ marginTop: 8, background: '#0A1E0A', border: '1px solid #1A3A1A', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: '#30D158', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>⚡ Last 24h</div>
                  {ch.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 3 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: item.color, flexShrink: 0, minWidth: 70 }}>{item.label}</span>
                      <span style={{ fontSize: 11, color: '#C8DFF0', lineHeight: 1.4 }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              ) : null; })()}
            </div>
          );
        })}
      </div>
    {overlaysJSX}
    </AppShell>
  );

  return overlaysJSX;
}
